import React from "react";
import { connect } from "react-redux";
import Link from "next/link";

import MainContainer from "../../components/mainContainer/index";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBookOpen,
    faClipboardList,
    faUsers,
    faShieldAlt,
    faShip,
    faMagic,
    faChartLine,
    faHeartbeat,
    faDragon
} from "@fortawesome/free-solid-svg-icons";

import style from "./style.scss";

// ════════════════════════════════════════════════════════════════
// Wiki content — hardcoded data, sin parser de Markdown. Para el
// changelog completo (más entradas que las que se renderean acá)
// ver PATCH_NOTES.md en el root del repo.
// ════════════════════════════════════════════════════════════════

const PATCH_NOTES = [
    {
        version: "v2026-07-08",
        title: "La Herrería: forjá lingotes con tus minerales",
        ticket: 329,
        category: "Features",
        bullets: [
            "[#329] Nuevo oficio: la Herrería. Ahora los minerales que extraés con el pico tienen uso — se funden en lingotes de Hierro, Plata y Oro.",
            "[#329] Necesitás el Martillo de Herrero. Equipalo como cualquier arma y hacele doble-click para abrir la Herrería (igual que la caña de pescar, el pico o el hacha).",
            "[#329] El Martillo de Herrero se compra en cualquier Herrero del mundo.",
            "[#329] Cada lingote cuesta 100 minerales del mismo metal. El modal te muestra cuántos minerales tenés y forjás con un click cuando llegás a 100.",
            "[#329] Las Espadas Argentum y demás recetas de forjado llegarán apoyadas sobre este sistema de lingotes.",
            "[#330] Se arreglaron los bordes de la costa: el agua animada ya no tapa la orilla donde el mar se junta con la tierra."
        ]
    },
    {
        version: "v2026-07-07",
        title: "Optimización de performance: el juego anda más liviano",
        ticket: 314,
        category: "Features",
        bullets: [
            "[#314] El servidor ya no procesa criaturas de mapas vacíos — solo las de mapas donde hay jugadores. Menos latencia en cada acción para todos.",
            "[#314] Se arregló una fuga que hacía que el juego se pusiera más pesado con el tiempo: cada vez que reconectabas quedaba un render viejo corriendo de fondo.",
            "[#315] El tráfico de red en zonas con mucha gente se redujo drásticamente — antes cada movimiento se re-procesaba una vez por cada jugador cercano (crecía al cuadrado). Esto elimina los tirones de ping en ciudades y wars.",
            "[#316] El HUD del juego re-dibuja menos veces por segundo, dando más FPS en combate."
        ]
    },
    {
        version: "v2026-07-06",
        title: "Inspeccionar jugadores, Sacerdote de la Muerte y más",
        ticket: 304,
        category: "Features",
        bullets: [
            "[#304] Click derecho sobre cualquier jugador → \"Inspeccionar\" abre un panel con su nivel, clase, raza, vida, maná y estadísticas de PvP (jugadores y criaturas matadas). También podés auto-inspeccionarte para ver ambos paneles lado a lado.",
            "[#304] El texto al hacer click sobre alguien se simplificó a solo \"Ves a X\" — el resto de la info ahora vive en el panel de inspección.",
            "[#304] Pedir /PERDON ya no borra tu historial de ciudadanos asesinados. Volvés a ser ciudadano, pero tu pasado queda visible para siempre (y el costo del perdón escala con tus kills).",
            "[#308] Nuevo Sacerdote de túnica roja en Nueva Esperanza: solo revive muertos, no cura. Si necesitás curación, buscá a otro clérigo.",
            "[#310] Ahora se puede pescar en la fuente central de Ullathorpe.",
            "[#311] Matar un Lobo reproduce un aullido de muerte."
        ]
    },
    {
        version: "v2026-07-04",
        title: "La Guarida del Bardo: radio comunitaria en vivo",
        ticket: 303,
        category: "Features",
        bullets: [
            "[#303] Nueva radio global sincronizada: todos los jugadores pueden escuchar la misma música al mismo tiempo, curada por un GM.",
            "[#303] Escribí /radio (solo, sin nada más) para sintonizar y escuchar. Volvé a escribirlo para volver a la música del mapa.",
            "[#303] Un GM sintoniza la música con /radio y un link de YouTube. Puede armar una cola de temas y saltar al siguiente.",
            "[#303] Cuando se acaba la cola, la radio sigue sola con recomendaciones parecidas al último tema — nunca queda en silencio.",
            "[#303] Mientras escuchás la radio, el control de volumen de música y el botón de música controlan la radio. Se ve \"🎵 Radio · <tema>\" en pantalla."
        ]
    },
    {
        version: "v2026-07-02",
        title: "Nuevo hechizo: Aceleración Arcana",
        ticket: 306,
        category: "Features",
        bullets: [
            "[#306] Aceleración Arcana acelera la velocidad de movimiento del objetivo durante 13 segundos.",
            "[#306] Es acumulable hasta 5 veces: cada vez que lo volvés a lanzar, la velocidad se duplica (×2, ×4, ×8, ×16, ×32) y el ícono del buff muestra el nivel de stack.",
            "[#306] Cuesta apenas 1 de maná pero requiere 101 de skill de Magia."
        ]
    },
    {
        version: "v2026-07-01",
        title: "Talar árboles + cambios en hechizos de dragón",
        ticket: 305,
        category: "Features",
        bullets: [
            "[#305] Nuevo oficio de recolección: Talar. Equipá un Hacha de Leñador, pará frente a un árbol y hacé doble-click para cortar Leña — igual que Minar o Pescar.",
            "[#305] Las 3 hachas de leñador (común, newbie y élfica) ahora son herramientas de talado, no armas de combate.",
            "[#309] Se removieron del juego los hechizos Llamado a Dragón Blanco y Llamado a Dragón Negro. Los personajes que los tenían aprendidos ya no los ven."
        ]
    },
    {
        version: "v2026-06-13-2",
        title: "Nuevo hechizo: Llamado a Dragón Negro (Asesino)",
        ticket: 299,
        category: "Features",
        bullets: [
            "[#299] El Asesino tiene su propio pet de clase: \"Llamado a Dragón Negro\". Cuesta 620 de maná e invoca un Dragón Negro de 250 HP — contraparte oscura del Dragón Blanco del Paladín.",
            "[#299] Mismas mecánicas que el Blanco: 250-300 de daño, ocupa los 3 slots del cap de mascotas. Lo distintivo es el tint negro del sprite.",
            "[#299] No podés tener Dragón Blanco y Negro a la vez — son mutuamente exclusivos por el sistema de petWeight."
        ]
    },
    {
        version: "v2026-06-13",
        title: "Marineros: viajes entre puertos por oro (sistema de ferry)",
        ticket: 321,
        category: "Features",
        bullets: [
            "[#321] Aparecen 6 Marineros NPCs en los puertos principales (Banderbill, Arkhein, Arghal, Nix, Lindos, Nueva Esperanza) que te llevan entre ciudades costeras instantáneamente.",
            "[#321] Click izquierdo sobre el Marinero abre un modal visual con la lista de destinos disponibles y el costo de cada uno.",
            "[#321] Viajes entre ciudades: 9.000 oro. Viaje a Nueva Esperanza: 15.000 oro (más lejano).",
            "[#321] Funciona estés navegando o no — si vas a una ciudad costera con barco equipado, el barco viaja con vos."
        ]
    },
    {
        version: "v2026-06-11-3",
        title: "IA del bot PvP: ahora pelea como jugador (poteo + esquiva + comportamiento por clase)",
        ticket: 317,
        category: "Features",
        bullets: [
            "[#317] El bot que spawneás con `/bot` para entrenar PvP ahora reacciona como un jugador real, no como secuencia robótica.",
            "[#317] Potea pociones rojas y azules de forma inteligente: rojas bajo 85% de HP, azules bajo 85% de maná (si es clase mágica).",
            "[#317] Comportamiento según la clase del char original: magos mantienen distancia y kitean, guerreros avanzan con movimiento errático para esquivar hechizos, híbridos mezclan ambos.",
            "[#317] Esquiva predictiva: si te ve castear, mueve perpendicular para esquivar el proyectil.",
            "[#317] Prioriza curarse el status (Devolver Movilidad) sobre todo lo demás si queda paralizado o inmovilizado."
        ]
    },
    {
        version: "v2026-06-11-2",
        title: "Balance HP: Enanos, Elfos y Drow ganan +1 HP cada nivel par",
        ticket: 316,
        category: "Balance",
        bullets: [
            "[#316] Antes solo Constitución ≥20 hacía diferencia de HP/nivel — Enano y Humano tenían el mismo HP, y Elfo/Drow lo mismo que Gnomo. Ahora las 5 razas se diferencian en supervivencia.",
            "[#316] Enano, Elfo y Drow ganan +1 HP extra cada nivel par (L2, L4, ..., L42). Equivale a +0.5 HP/nivel promedio.",
            "[#316] HP total a L42: Guerrero Enano 451, Humano 430, Elfo/Drow 410, Gnomo 389. Cada salto de raza ~20 HP a L42.",
            "[#316] Retroactivo: 61 chars existentes de razas Enano/Elfo/Drow recibieron el HP nuevo en la migración."
        ]
    },
    {
        version: "v2026-06-10-3",
        title: "Elementales rebalanceados: Fuego solo castea, Tierra inmoviliza",
        ticket: 314,
        category: "Balance",
        bullets: [
            "[#314] Elemental de Fuego ya no pega melee — castea Tormenta de Fuego (45-55 daño mágico) cada 2.5s a distancia. Útil contra grupos o targets que no se le pegan al cuerpo.",
            "[#314] Elemental de Tierra pega 50-60 melee (la mitad de antes) pero tiene 80% chance de Inmovilizar al target. Útil para controlar PvP / kitear.",
            "[#314] Elemental de Agua y Elemental Anciano sin cambios — quedan como referencia del \"elemental clásico\" balanceado.",
            "[#314] Cada elemental tiene ahora identidad gameplay propia — la elección de qué invocar depende del escenario."
        ]
    },
    {
        version: "v2026-06-10-2",
        title: "Sistema de Clanes: fundación, invitaciones, solicitudes y modal",
        ticket: 313,
        category: "Features",
        bullets: [
            "[#313] Los clanes existen como feature gameplay completa. Hay 5 tipos: Mixto, Solo Armada, Solo Caos, Ciudadanos+Armada, Criminales+Caos.",
            "[#313] Para fundar: nivel 35+ y 10.000.000 oro. Elegir nombre + tipo (vos tenés que cumplir el requisito del tipo elegido).",
            "[#313] Nuevo botón \"CLAN\" en el HUD con modal visual. Sin clan: tabs Unirse (mandar solicitud) / Fundar. Con clan: stats, lista de miembros online/offline, solicitudes pendientes (líder), botón salir/disolver.",
            "[#313] Invitaciones: `/clan invitar <nombre>` desde el modal del líder. El invitado tipea `/clan aceptar` o `/clan rechazar` (60s).",
            "[#313] Caps por nivel del clan: L1 6 miembros, L2 9, L3 12, L4 15. Todos arrancan en L1.",
            "[#313] En el viewport, debajo de tu char aparece tu tag de clan. En el minimap los miembros del clan en tu mapa se ven como dots ámbar."
        ]
    },
    {
        version: "v2026-06-09",
        title: "Sistema de Facciones: enlistarse en Armada Real o Legión del Caos",
        ticket: 311,
        category: "Features",
        bullets: [
            "[#311] Tancredo de Hauteville (Banderbill) y Demonio Legionario (Arkhein) finalmente cumplen lo que sus diálogos prometían: ahora podés `/enlistar`.",
            "[#311] Armada Real: tenés que ser ciudadano, tener 100 criminales matados y estar al lado de Tancredo en Banderbill. Si te aceptan, tu nombre pasa de azul a cian.",
            "[#311] Legión del Caos: tenés que ser criminal, tener 300 muertes combinadas y estar al lado del Demonio en Arkhein. Si te aceptan, tu nombre pasa de rojo a rojo oscuro.",
            "[#311] Una vez enlistado, el flag persiste entre sesiones. Si un Armada se vuelve criminal, pierde el alistamiento automáticamente."
        ]
    },
    {
        version: "v2026-06-05-3",
        title: "Nuevo hechizo: Llamado a Dragón Blanco (Paladín)",
        ticket: 298,
        category: "Features",
        bullets: [
            "[#298] Paladines tienen un hechizo nuevo: \"Llamado a Dragón Blanco\". Cuesta 620 de maná (vs los 1100 de Llamado a Uhkrul del Mago) e invoca un Dragón Blanco de 250 HP.",
            "[#298] El Dragón Blanco es una mascota pesada: ocupa los 3 slots completos del cap de mascotas. Si lo invocás, no podés tener otras pets; si tenés pets activas, no podés invocarlo.",
            "[#298] El sistema interno de mascotas ahora soporta \"peso\" por criatura — futuros pets pueden ser medianos (peso 2) o pesados (peso 3) según diseño."
        ]
    },
    {
        version: "v2026-06-05-2",
        title: "Bardos pueden tocar instrumentos para recuperar maná",
        ticket: 297,
        category: "Features",
        bullets: [
            "[#297] Los Laúd / Flauta / Tambor (y sus tiers Mágico y Élfico) ahora son funcionales. Solo Bardos pueden tocarlos haciendo doble-click sobre el item.",
            "[#297] Cada toque recupera maná según el tier: 5 (común) · 10 (mágico) · 15 (élfico). Cooldown de 5 segundos para que no se transforme en farmeo de maná infinito.",
            "[#297] El sonido del instrumento se escucha en el viewport — todos los chars cerca saben que un Bardo está tocando (útil para identificar Bardos invisibles tocando para recuperar maná).",
            "[#297] Antes los 9 instrumentos eran items inertes y los 6 mágicos/élficos estaban mal categorizados internamente como anillos. Bardo ahora es una clase con identidad propia."
        ]
    },
    {
        version: "v2026-06-05",
        title: "Fix: líneas negras entre tiles al caminar",
        ticket: 296,
        category: "Bug fixes",
        bullets: [
            "[#296] Algunos usuarios reportaban líneas negras finitas entre tiles cuando el personaje se movía. Era un bug de sub-pixel rendering: el browser dejaba un gap del tamaño de una fracción de pixel entre tiles adyacentes, que se veía negro porque el canvas del fondo estaba en modo opaco.",
            "[#296] El fix está activo por default — el render ahora alinea los tiles al pixel exacto y desactiva el suavizado de imagen (pixel art crisp).",
            "[#296] Si por alguna razón querés el comportamiento viejo (render con sub-pixel float), podés apagarlo en Configuración → \"Bordes nítidos de tiles\". El toggle persiste por browser."
        ]
    },
    {
        version: "v2026-06-04-4",
        title: "Sastres ya no venden Teleport Sangre",
        ticket: 294,
        category: "Balance",
        bullets: [
            "[#294] El Teleport Sangre era un consumible reservado para GMs (lo usan para crear portales `/creartp rojo`). Hasta ahora 5 Sastres lo vendían al público — removido del catálogo de los 5.",
            "[#294] Si ya tenés algunos en tu inventario, los conservás — solo se cortó la oferta nueva."
        ]
    },
    {
        version: "v2026-06-04-3",
        title: "Chat: ahora podés seleccionar texto y mandar links clickeables",
        ticket: 293,
        category: "Quality of Life",
        bullets: [
            "[#293] El chat console ahora permite seleccionar texto con el mouse — copy/paste de nombres, drops, errores y links funciona normal.",
            "[#293] URLs (http/https), www, invitaciones de Discord (discord.gg/...) y aoweb.net se renderean automáticamente como links clickeables.",
            "[#293] Click en un link abre en nueva pestaña sin afectar el juego (target=_blank, noopener).",
            "[#293] Funciona también dentro de whispers: `/w Asuka mira esto https://discord.gg/YsKZjtVuz8` ahora le manda un link real."
        ]
    },
    {
        version: "v2026-06-04-2",
        title: "Fix crítico: los keybindings y macros se perdían al deslogear",
        ticket: 292,
        category: "Bug fixes",
        bullets: [
            "[#292] Bug fix crítico: si cambiabas teclas o macros y deslogeabas, al volver a entrar las tenías perdidas. Causa: el server pisaba en cada save los valores con los del login original.",
            "[#292] A partir de ahora los 4 tipos de keybindings (acciones, hechizos, items, macros) persisten correctamente entre sesiones y entre browsers — como debería desde el ticket #281.",
            "[#292] Si tenías teclas o macros configurados antes de este patch y las habías 'perdido', ahora podés volver a configurarlas y van a quedar guardadas."
        ]
    },
    {
        version: "v2026-06-04",
        title: "Fix: equipar Barca exige agua adyacente",
        ticket: 290,
        category: "Bug fixes",
        bullets: [
            "[#290] Fix: ya no se puede equipar la Barca parado en tierra firme — ahora exige tile de agua adyacente (norte/sur/este/oeste), como en el AO clásico.",
            "[#290] Antes se podía equipar la Barca parado en plena Ullathorpe y caminar por todo el mapa con el sprite del bote. Rompía la inmersión y abría exploits cosméticos.",
            "[#290] El comportamiento de bajar del barco no cambia."
        ]
    },
    {
        version: "v2026-06-04",
        title: "Chat con tabs: General / Combate / Chat — leer mensajes sin combat log de fondo",
        ticket: 283,
        category: "Quality of Life",
        bullets: [
            "El chat console ahora tiene 3 tabs minimalistas: General (todo, default), Combate (daños y status), Chat (whispers, chat público, GM, mensajes del servidor).",
            "Tab Chat muestra una burbuja con notificación cuando llega un whisper (lila), un mensaje de GM (verde) o un broadcast del servidor (ámbar) — el color del badge es el del último mensaje recibido.",
            "Tab Combate no muestra burbuja — el flujo de daños es continuo y no necesita aviso.",
            "Chatbox crecida para mostrar ~5 mensajes sin cortes (antes ~3).",
            "Mensajes \"Te has escondido\", \"Tienes la mana al máximo\", \"Has recuperado X\", \"Ves a [char]\" y similares ahora aparecen en Combate (no en Chat).",
            "Nombre del char y los iconitos del HUD reorganizados — iconos arriba a la derecha, nombre alineado a la izquierda."
        ]
    },
    {
        version: "v2026-06-03",
        title: "Performance: server más liviano en zonas pobladas (~16× speedup en broadcasts)",
        ticket: 282,
        category: "Performance",
        bullets: [
            "Optimización interna del server: los broadcasts de movimiento, animaciones y acciones a otros jugadores cercanos ahora son ~16× más eficientes.",
            "Resultado: el ping debería sentirse más estable cuando hay muchas criaturas o jugadores en la misma zona, y el server tiene mucho más margen para escalar a más usuarios concurrentes.",
            "Cero cambio visible al gameplay — solo más fluido bajo carga."
        ]
    },
    {
        version: "v2026-06-03",
        title: "Keybindings (acciones, hechizos, items) ahora persisten por personaje y entre browsers",
        ticket: 281,
        category: "Quality of Life",
        bullets: [
            "Antes los keybindings vivían solo en localStorage — si cambiabas de browser o hacías clear storage, los perdías.",
            "Ahora cada personaje guarda sus teclas en el server. Login desde otro browser → mismos bindings. Hacer clear storage → siguen ahí al re-loguear.",
            "Aplica a las 4 fuentes: macros, acciones del modal de Configuración (W/A/S/D/atacar/etc), teclas asignadas a hechizos, y teclas asignadas a items del inventario."
        ]
    },
    {
        version: "v2026-06-03",
        title: "Pets: nuevo sistema con modo Defensivo / Ofensivo + click despawn",
        ticket: 280,
        category: "Features",
        bullets: [
            "Click izquierdo en el buff de la mascota → la pet desaparece. Útil para liberar un slot del cap de 3 e invocar otra.",
            "Click derecho en el buff → mini-menu con Defensivo / Ofensivo. Default ahora es Ofensivo (la pet busca enemigos sola en un radio chico — ~6 tiles).",
            "Defensivo: la pet solo ataca lo que vos atacaste, o lo que te ataque a vos. Mantiene una cola de targets — si el primero muere, vuelve al siguiente automáticamente.",
            "Cuando tu pet ataca a una criatura, esa criatura ahora prioriza pegarle a la pet (no a vos). Útil para tankear.",
            "Las pets nunca atacan jugadores reales por iniciativa propia — solo si vos los atacaste o ellos te atacaron primero.",
            "Cada tipo de mascota (Lobo, Zombie, Dragón Rojo, Elementales) tiene su propio iconito en el buff. Badge encima del icono: escudo (defensivo) o espadita (ofensivo)."
        ]
    },
    {
        version: "v2026-06-03",
        title: "Hechizos de invocación arreglados: Llamado a la Naturaleza y Llamado Nigromante",
        ticket: 279,
        category: "Bugfixes",
        bullets: [
            "Llamado a la Naturaleza (Druida, skill 40) ahora invoca correctamente 2 Lobos como mascotas. Antes consumía 120 de mana pero no aparecía nada.",
            "Llamado Nigromante (skill 51) ahora invoca correctamente 2 Zombies como mascotas. Antes consumía 400 de mana sin efecto.",
            "Funcionan igual que los Elementales del ticket de invocaciones: te siguen, atacan al target que vos ataques, duran 3 minutos, y cuentan hacia el cap de 3 mascotas concurrentes.",
            "Si ya tenés 3 mascotas y casteás, no se invocan más y NO se descuenta mana."
        ]
    },
    {
        version: "v2026-06-02",
        title: "Mapa más simple: ahora M abre directo el mapamundi (sin paso intermedio)",
        ticket: 278,
        category: "Quality of Life",
        bullets: [
            "Antes apretar M te mostraba primero el mapa de la zona donde estás, y recién al apretar M de nuevo (o right-click) ibas al mapamundi. Ahora M abre directo el mapamundi.",
            "Desde el mapamundi: click en cualquier celda para ver ese mapa en detalle. M (o right-click) para cerrar/volver.",
            "Se eliminó la feature de mostrar enemigos en el mapa (los puntitos naranjas sobre el mapa) — incluyendo el setting en Configuración para activarla."
        ]
    },
    {
        version: "v2026-06-02",
        title: "Worldmap: Entrada de Arghal y Ciudad de Arkhein en sus lados correctos",
        ticket: 277,
        category: "Bugfixes",
        bullets: [
            "Mismo enfoque que los patches anteriores: Entrada de Arghal (mapa 156) ahora aparece junto a la zona de Arghal (al sur del mapamundi) y Ciudad de Arkhein (195) junto a la zona de Arkhein (al norte).",
            "Cierra el alineamiento visual de la zona Arghal/Arkhein. Cero impacto al gameplay — sólo cambio visual.",
            "Ctrl+Shift+R para refrescar."
        ]
    },
    {
        version: "v2026-06-02",
        title: "Worldmap: zona Arghal/Arkhein completamente alineada",
        ticket: 276,
        category: "Bugfixes",
        bullets: [
            "Mismo enfoque que el patch anterior: el mapa 151 (Ciudad de Arghal) y el 196 (Ciudad de Arkhein) aparecían en posiciones del mapamundi que no coincidían con sus conexiones reales.",
            "Ahora las 4 ciudades de la zona (Muelle/Ciudad de Arghal + Puerto/Ciudad de Arkhein) tienen ubicaciones visuales coherentes con la navegación in-game.",
            "Cero impacto al gameplay. Ctrl+Shift+R para refrescar."
        ]
    },
    {
        version: "v2026-06-02",
        title: "Worldmap: posición visual de Muelle de Arghal corregida",
        ticket: 275,
        category: "Bugfixes",
        bullets: [
            "El mapa 150 (Muelle de Arghal) aparecía en una posición del mapamundi que no coincidía con sus conexiones reales. Su vecino oeste decía 151 (Ciudad de Arghal) pero geográficamente el tileExit apuntaba a una zona distinta.",
            "Ahora la celda visual de 150 está en la posición que matchea con sus tileExits server-side (vecinos 92/221/223/196).",
            "Cero impacto al gameplay — solo se corrigió la visualización. Ctrl+Shift+R para refrescar."
        ]
    },
    {
        version: "v2026-06-02",
        title: "Mar Oriental: ahora el worldmap refleja correctamente la navegación",
        ticket: 274,
        category: "Bugfixes",
        bullets: [
            "Antes el mapamundi mostraba al mapa 139 al este del 138, pero en realidad no se podía cruzar entre esos dos. Y al sur, 122 conectaba con 139 pero el mapamundi no lo reflejaba.",
            "Ahora el mapamundi está alineado con cómo realmente fluye la navegación: 139 aparece como vecino sur (al este del 122), no al este del 138.",
            "Cero impacto al gameplay — solo se corrigió la visualización. No hace falta restart, basta con Ctrl+Shift+R."
        ]
    },
    {
        version: "v2026-06-02",
        title: "Teclas y macros: ahora podés reasignar libremente",
        ticket: 273,
        category: "Quality of Life",
        bullets: [
            "Antes si querías ponerle una tecla a una acción/macro/hechizo/item, pero esa tecla ya estaba asignada a otro slot, el juego te rechazaba (te tiraba un alert o un error y te pedía que probaras otra).",
            "Ahora simplemente la última asignación gana: ponés la tecla en el nuevo slot y el slot anterior queda libre automáticamente. No más andar buscando dónde estaba.",
            "En el modal de macros además ves un hint del estilo \"Al guardar, esta tecla se liberará de: Macro 3\" antes de confirmar, así sabés exactamente qué se va a desbindear.",
            "Funciona en las 4 fuentes: acciones (atacar/equipar/agarrar/etc), macros (slots 1-9), hechizos (right-click → asignar tecla) e inventario (right-click → asignar tecla)."
        ]
    },
    {
        version: "v2026-06-02",
        title: "Golem de Arena: tenés que matar también a los Djinn para que respawnee",
        ticket: 272,
        category: "Balance",
        bullets: [
            "Antes el Golem de Arena respawneaba al matarlo aunque sus Djinn de Viento siguieran vivos. Eso era farm trivial — matar al Golem solo + ignorar Djinn = exp x 200k cada vez sin riesgo.",
            "Ahora el Golem solo respawnea cuando él Y los 3 Djinn estén muertos. Tenés que comprometerte al combate completo.",
            "Si matás primero al Golem, queda invisible / inactivo hasta que el último Djinn caiga — recién ahí reaparece en su ruta de patrulla.",
            "Cuidado al desengancharte mid-pelea: los Djinn quedan vivos en el mapa esperando — el Golem no va a respawnear hasta que alguien los mate."
        ]
    },
    {
        version: "v2026-06-02",
        title: "Djinn de Viento ahora caminan por tierra y agua",
        ticket: 271,
        category: "Bugfixes",
        bullets: [
            "Los Djinn de Viento (los que invoca el Golem de Arena al 50% HP) quedaban inertes cuando aparecían — no se movían ni atacaban.",
            "Bug viejo de configuración: eran 'solo agua' por su origen, pero como criaturas espirituales/aéreas deberían poder transitar cualquier terreno.",
            "Ahora son anfibios: caminan tanto por tierra como por agua. Si los invoca el Golem en el desierto, te van a perseguir y castear sin problema."
        ]
    },
    {
        version: "v2026-06-02",
        title: "Golem de Arena: nuevo boss en el desierto con patrulla e invocación",
        ticket: 270,
        category: "Features",
        bullets: [
            "Apareció el Golem de Arena en el desierto al sur de Ullathorpe — un boss de 35.000 HP que da 200.000 de exp al matarlo.",
            "Patrulla una ruta fija cíclica recorriendo el mapa. Cuando no hay nadie cerca, camina entre sus puntos de paso solo.",
            "Al bajar al 50% de vida, invoca 3 Djinn de Viento a su alrededor que casteon paralizar y descarga eléctrica. Cuidado con el cambio de fase.",
            "Pega 300-600 de daño por ataque — no es para ir solo si vas con poco nivel.",
            "Drops como el Golem regular (gemas y oro).",
            "Cuando lo matás, respawnea en algún punto al azar de su ruta de patrulla."
        ]
    },
    {
        version: "v2026-05-31",
        title: "Hechizos de invocación: Elementales como mascotas que pelean por vos",
        ticket: 258,
        category: "Features",
        bullets: [
            "Los hechizos Invocar Elemental de Fuego/Agua/Tierra ahora funcionan como mascotas reales. Antes no hacían nada útil, y el de Agua incluso hacía daño por bug.",
            "Cada Elemental que invoques aparece junto a tu char y te sigue automáticamente.",
            "Cuando atacás a una criatura o jugador, tu mascota va a atacar al mismo target con melee. Si cambiás de target, ella cambia también.",
            "Podés tener hasta 3 mascotas invocadas al mismo tiempo.",
            "Cada mascota dura 3 minutos. El timer se muestra en la barra de buffs arriba a la derecha con un ícono del color del elemento (azul/rojo/marrón).",
            "El kill de la mascota cuenta como tuyo: ganás XP, el drop, y el contador de criaturas asesinadas sube.",
            "Si te desconectás, cambiás de mapa o morís, tus mascotas desaparecen automáticamente."
        ]
    },
    {
        version: "v2026-05-31",
        title: "Runa de teletransportación ahora se puede agarrar del suelo",
        ticket: 255,
        category: "Bugfixes",
        bullets: [
            "Si una Runa de Teletransportación caía al suelo (al morir, al tirarla, drop PvP), no se podía levantar de vuelta — quedaba ahí para siempre.",
            "Era un bug heredado de configuración: la Runa tenía un flag interno que la marcaba como 'fija' (mismo flag que usa Árbol, Puerta, Yunque, Fogata), por lo que el server rechazaba el agarre.",
            "Ahora cualquier Runa dropeada se levanta normal con la tecla de agarrar."
        ]
    },
    {
        version: "v2026-05-31",
        title: "Equipar / desequipar items arriba del barco",
        ticket: 252,
        category: "Quality of Life",
        bullets: [
            "Ahora podés equipar y desequipar armaduras, armas, escudos, cascos y flechas mientras navegás. Antes había que desembarcar para cualquier cambio de loadout.",
            "El sprite visible del barco no cambia mientras estás navegando — los testigos siguen viendo la Barca / Galera / Galeón / Fragata Fantasmal igual.",
            "Lo que equipaste se aplica visualmente al desembarcar: bajás del barco y el char aparece con el nuevo equipo automáticamente.",
            "Restricciones de clase y raza siguen aplicando navegando igual que en tierra (no podés equipar un item que tu clase no puede)."
        ]
    },
    {
        version: "v2026-05-31",
        title: "Morir en el agua ahora también dropea los items",
        ticket: 251,
        category: "Balance",
        bullets: [
            "Antes, si morías navegando (en cualquier barco), conservabas todo el inventario al revivir — el mar era zona segura de facto contra perder loot.",
            "Ahora morir en agua dropea los items igual que morir en tierra. Los items caen en el tile de muerte (puede ser agua) y solo otro char navegando los puede recoger.",
            "Si tenías un barco equipado al morir, también cae al agua junto con el resto del inventario — recogible por otro navegante.",
            "Items con flag newbie o noSeCae siguen sin dropear (intacto desde siempre).",
            "Las zonas seguras (arenas, sparring) siguen siendo safe — morir ahí no dropea nada."
        ]
    },
    {
        version: "v2026-05-31",
        title: "Click sobre personaje: ahora muestra criaturas y kills",
        ticket: 250,
        category: "UX",
        bullets: [
            "Al hacer click sobre otro personaje, el tooltip del chat ahora suma dos bloques de estadísticas de combate.",
            "Criaturas asesinadas: total de mobs no-player matados por ese char (usaba un contador que ya se persistía pero no se mostraba).",
            "Matados: total de PvP kills, desglosado en Ciudadanos vs Criminales (mismo dato del ranking público).",
            "Formato: 'Ves a [Nombre] [Clase] [Nivel: X] - Ciudadano [Criaturas asesinadas: 1000] [Matados: 20 | Ciudadanos: 10 | Criminales: 10]'."
        ]
    },
    {
        version: "v2026-05-31",
        title: "Pociones más baratas",
        ticket: 249,
        category: "Balance",
        bullets: [
            "Bajaron los precios de compra y venta de las 4 pociones de combate. Ahora son más accesibles para el grind temprano y la economía general.",
            "Poción Roja (vida): compra de 10 → 5 oro, venta de 6 → 3 oro.",
            "Poción Azul (maná): compra de 12 → 6 oro, venta de 8 → 4 oro.",
            "Poción Verde (agilidad): compra de 90 → 45 oro, venta de 60 → 30 oro.",
            "Poción Amarilla (fuerza): compra de 60 → 30 oro, venta de 40 → 20 oro.",
            "Las pociones newbie del starter kit y las pociones Violeta / Negra no se tocaron."
        ]
    },
    {
        version: "v2026-05-31",
        title: "Bichos casteadores: Orco Brujo, Galeón Fantasmal, Djinn de Viento",
        ticket: 248,
        category: "Features",
        bullets: [
            "Tres enemigos suman habilidades mágicas a su kit, en línea con Bruja y Medusa que ya casteaban.",
            "Orco Brujo: tira Proyectil Mágico (30-35 daño) cada ~12 segundos.",
            "Galeón Fantasmal: tira Tormenta de Fuego (45-55 daño) cada ~18 segundos. Cuidado en los combates navales.",
            "Djinn de Viento: alterna Paralizar (cada ~20s) y Descarga Eléctrica (55-85 daño, cada ~14s). Dos hechizos con cooldowns independientes.",
            "Cada instancia tiene su propio reloj de cooldown — no todos los Orcos Brujos castean al mismo tiempo (los spawns arrancan desfasados).",
            "Casco con defensa mágica (Casco Mágico, Yelmo Vikingo) reduce el daño de los spells igual que en PvP.",
            "Detalle: el guard de 'no spammear paralizar' solo aplica al Djinn (Paralizar) — los spells de daño se castean igual sobre un target ya paralizado."
        ]
    },
    {
        version: "v2026-05-31",
        title: "Inspeccionar enemigos: experiencia y oro reales",
        ticket: 247,
        category: "Bugfixes",
        bullets: [
            "El modal de inspeccionar NPC (click derecho sobre un enemigo) mostraba números que no coincidían con lo que recibías realmente al matarlo: la experiencia aparecía 5× menor que la real y el oro era 0 aunque el bicho dropeara monedas.",
            "Ahora el modal muestra los valores efectivos: experiencia post-multiplicador (×5, ×10 con doble exp activo) y oro post-multiplicador (×3, ×6 con doble oro activo). Lo que ves es lo que recibís.",
            "Bonus: el evento de doble oro (/dobleoro) antes solo afectaba al kill PvP — los drops de monedas de NPCs no se duplicaban. Ahora se duplican también, consistente con el nombre del evento.",
            "Las probabilidades de drop por slot del enemigo (90% / 10% / 1% / 0.1% / 0.01%) no cambiaron — eran correctas, solo no se reflejaban del lado del oro."
        ]
    },
    {
        version: "v2026-05-31",
        title: "Pantalla completa y arrow-keys que no scrollean",
        ticket: 246,
        category: "UX",
        bullets: [
            "Nuevo botón de pantalla completa al lado de la tuerca de configuración (parte derecha del HUD). Toggle de fullscreen al instante — ícono cambia entre expandir y contraer según el estado.",
            "También sincroniza el ícono si salís de pantalla completa con Esc o F11.",
            "Las teclas ↑ y ↓ (caminar) ya no scrollean la página entera cuando el HUD es más alto que el viewport (modales abiertos, browser zoom alto). El personaje camina sin que se mueva la pantalla.",
            "Funcionalidad de las flechas dentro de inputs (chat, formularios) intacta — solo se bloquea el scroll cuando el foco está afuera de un campo de texto."
        ]
    },
    {
        version: "v2026-05-31",
        title: "Mapa 272 más tranquilo + cleanup interno de Ullathorpe",
        ticket: 244,
        category: "Quality of Life",
        bullets: [
            "El mapa 272 tenía un lobo solitario rondando. Reemplazado por un NPC estático (estilo Fethresor) y se sumó un Sacerdote más como referencia visual. Mapa ahora 100% tranquilo: 1 NPC estático + 3 Sacerdotes.",
            "Limpieza interna preventiva del mapa 1 (Ullathorpe): aunque la ciudad ya estaba libre de hostiles desde el patch anterior, restaban 15 celdas con referencias antiguas a Ratas y Orcos. Removidas para evitar que vuelvan si en algún momento se regenera el JSON de NPCs."
        ]
    },
    {
        version: "v2026-05-31",
        title: "Resucitar: ahora exclusivo de Clérigos y Druidas",
        ticket: 243,
        category: "Balance",
        bullets: [
            "El hechizo Resucitar queda restringido a las clases sagradas/naturales de AO. Magos, Bardos, Paladines y Asesinos ya no pueden aprenderlo ni comprarlo.",
            "Si intentás aprender desde pergamino o comprar el pergamino en una tienda y tu clase no es Clérigo o Druida, el server bloquea con mensaje: 'Solo Clérigos y Druidas pueden aprender este hechizo'.",
            "Purga ejecutada: los chars no-Clérigo/Druida que ya tenían Resucitar aprendido fueron despojados del slot. El resto de sus hechizos quedó intacto. (Afectó a 1 personaje.)",
            "Si tu clase autorizada ya tiene Resucitar, no cambia nada — seguís pudiendo castearlo igual que antes."
        ]
    },
    {
        version: "v2026-05-31",
        title: "Cazadores empiezan con 1000 flechas (era 50)",
        ticket: 242,
        category: "Quality of Life",
        bullets: [
            "Las 50 flechas newbie del starter kit del Cazador se agotaban en pocos minutos de grind temprano. Aumentado a 1000 — alineado con la cantidad de pociones newbie del kit universal (200 c/u).",
            "Sigue siendo Flecha Newbie (no dropeable ni tradeable). Aplica a cazadores nuevos — los existentes conservan el stock que tengan en el inv."
        ]
    },
    {
        version: "v2026-05-30",
        title: "Fix: cadena de mapas al norte de Banderbill",
        ticket: 241,
        category: "Bugfixes",
        bullets: [
            "Subiendo al norte desde la afuera de Banderbill (171), la cadena correcta es 171 → 257 → 258 → 259 → 260 → 261 (Veril). Pero el mapamundi tenía esas posiciones embarulladas — saltaba entre mapas que no eran vecinos.",
            "También había un mapa fantasma 453 que apareció en el grid pero no existe server-side. Removido.",
            "Estado del mapamundi en general: el audit completo encontró ~175 inconsistencias entre el mapamundi del cliente y los tile-exits reales del server. Este fix cubre la cadena que reportaste; el resto queda como follow-up grande."
        ]
    },
    {
        version: "v2026-05-30",
        title: "Ciudades sin hostiles: Ullathorpe y Lindos limpias",
        category: "Quality of Life",
        bullets: [
            "Ullathorpe ahora no tiene Ratas Salvajes ni Serpientes patrullando. Los chars nuevos pueden caminar por la capital sin que les coma una serpiente al toque.",
            "Lindos también quedó limpio de Cuervos Salvajes y Tortugas Gigantes.",
            "Los animales que se quedan (Vaca, Gallo, Cerdo, Conejo, Pato, Gran Águila) no atacan — solo retalían si vos les pegás. Sirven para grind de cazadores y dan ambiente.",
            "Arkhein, Fuerte Pretoriano y Aldea Pretoriana se mantienen con sus hostiles — son territorio del Caos y zonas fronterizas, no ciudades 'seguras'.",
            "Total: 20 hostiles agresivos removidos de las ciudades amistosas."
        ]
    },
    {
        version: "v2026-05-30",
        title: "Nueva skill: Ocultarse (tecla O)",
        category: "Features",
        bullets: [
            "Nueva habilidad de stealth manual. Apretás la tecla O y tirás un chance de 50% de volverte invisible durante 5 segundos.",
            "Spameable: si fallás, podés volver a intentarlo de inmediato (hay un mini-cooldown anti-flood de 600ms).",
            "Mientras estás oculto nadie te ve — ni otros jugadores, ni NPCs hostiles. Mismo efecto visual que el hechizo de Invisibilidad, con el ícono de buff y countdown arriba.",
            "Se cancela en cuanto hacés algo: atacar (melee, hechizo a otro target, disparar arco), recibir daño (de un jugador o de un NPC), o caminar un solo tile. También se cae sola a los 5 segundos.",
            "Pensada para PvP: emboscadas, fuga, reposicionamiento. Si querés moverte invisible necesitás el hechizo (que sigue intacto).",
            "Disponible para todas las clases. La tecla es reconfigurable desde el modal de teclas, o podés disparar con /ocultar en el chat."
        ]
    },
    {
        version: "v2026-05-29",
        title: "Fix: items invisibles sobre tiles de fuego (Dungeon Inferno)",
        category: "Bugfixes",
        bullets: [
            "Bug heredado del engine original: si un item caía sobre un tile con animación de fondo (fuego, agua, lava), no se veía visualmente aunque estuviera en el suelo. El click confirmaba el item pero no aparecía dibujado.",
            "Pasaba notoriamente en Dungeon Inferno con todos los items single-frame (pociones, oro, armas comunes) sobre los tiles de fuego.",
            "Fix: el render de los items ahora va en la misma capa que el fuego, dibujado encima, justo antes de los personajes. Orden correcto: fondo → fuego → item → char → techos.",
            "Items en tiles normales siguen exactamente igual."
        ]
    },
    {
        version: "v2026-05-29",
        title: "Nuevo modal de comerciante — más limpio y moderno",
        category: "UX",
        bullets: [
            "Rediseñado todo el modal de los NPC mercaderes. Ahora usa el mismo lenguaje visual que el modal del banco: gradient oscuro, acento dorado tenue, sombras profundas.",
            "Preview del item seleccionado arriba: imagen + nombre + info (daño/defensa) + precio.",
            "Botones Comprar y Vender ahora se desactivan cuando no tiene sentido la acción (sin selección, sin oro, item equipado) y muestran tooltip explicando por qué.",
            "Botones + / − de cantidad funcionan (antes eran decorativos). El input es numérico y no acepta letras.",
            "Esc o click afuera del modal lo cierran. Al volver a abrirlo arranca limpio.",
            "Performance: el modal solo se redibuja cuando algo de la transacción cambia. Combate o regen de mana en paralelo no lo tocan."
        ]
    },
    {
        version: "v2026-05-29",
        title: "Limpieza interna del servidor (crashes-zombi y dead code)",
        category: "Bugfixes",
        bullets: [
            "Bundle de 3 fixes server-side identificados en un scan de bugs. Ninguno afectaba al gameplay pero saturaban los logs con stacktraces inútiles cuando llegaban packets viejos de chars que ya se habían desconectado.",
            "Borrado de una función vieja de validación que tenía 60 líneas de checks comentados y a la que nadie llamaba.",
            "Guards defensivos en 3 acciones (toggle seguro, cambio de dirección, equipar item) para que retornen limpio si el char ya se desconectó.",
            "Guard en compra a mercaderes para que rechace slots inválidos sin crashear."
        ]
    },
    {
        version: "v2026-05-29",
        title: "Packets de comerciante y banco más livianos",
        category: "Performance",
        bullets: [
            "Misma optimización del banco/inventario de antes, pero ahora aplicada también a abrir un NPC mercader y al modal completo del banco.",
            "Antes el server mandaba el nombre completo de cada item como texto al abrir; ahora solo manda el ID y el cliente resuelve el nombre solo.",
            "Ahorro estimado: ~200-1300 bytes menos por apertura, dependiendo de cuántos items hay.",
            "Abrir un mercader o el banco se siente un toque más snappy en conexiones lentas."
        ]
    },
    {
        version: "v2026-05-29",
        title: "Identificadores internos más livianos",
        category: "Performance",
        bullets: [
            "Los IDs internos de personajes y NPCs cambiaron de un formato grande (8 bytes) a uno chico (4 bytes).",
            "Esto reduce a la mitad el peso de muchísimos paquetes del juego: cada movimiento, cada hit, cada efecto visual sobre otro char/NPC.",
            "Ahorro estimado: ~3 KB/s/jugador. En zonas pobladas o conexiones lentas se siente más fluido.",
            "Cambio invisible al ojo — todo sigue jugándose igual."
        ]
    },
    {
        version: "v2026-05-29",
        title: "Render de items del piso optimizado",
        category: "Performance",
        bullets: [
            "Antes: caminar por una ciudad revisaba ~1000 casillas vacías por frame para ver si había algo en el piso.",
            "Ahora: se mantiene una lista chica de las posiciones que tienen items, y solo esas se dibujan.",
            "Speedup estimado: 5-10× menos chequeos por frame durante movement. Notable en zonas pobladas (Ullathorpe, Banderbill, dungeons llenas de cofres).",
            "Cero glitches visuales: items estáticos (carteles, puertas, cofres) y drops dinámicos (lo que tira un mob al morir, lo que dropea un player) siguen idénticos."
        ]
    },
    {
        version: "v2026-05-29",
        title: "Packets de inventario más livianos",
        category: "Performance",
        bullets: [
            "Antes: el servidor te mandaba el nombre de cada item del inventario como texto en cada login, equip, drop, pickup, depósito y retiro del banco.",
            "Ahora: el servidor solo manda el ID del item — el cliente ya tiene el catálogo cargado y resuelve el nombre solo.",
            "Ahorro: ~300-500 bytes menos por cada login + cada cambio de inventario. El login en conexiones lentas se siente un poco más snappy.",
            "Bonus: arreglado un bug viejo del banco donde el tooltip del lado del inventario decía 'undefined'."
        ]
    },
    {
        version: "v2026-05-29",
        title: "HUD más liviano en combate (continuación)",
        category: "Performance",
        bullets: [
            "Cerramos la última fase del refactor: ahora la caja del inventario y hechizos tampoco se redibuja cuando solo cambia HP/Mana.",
            "El HUD queda particionado en 3 islas (Stats, Mapa, Inventario/Hechizos) que reaccionan solo a sus inputs reales.",
            "Drag/drop de items, equip/desequip, lanzar y reordenar hechizos: todo funciona igual, pero sin trabajo desperdiciado de render en cada hit del combate."
        ]
    },
    {
        version: "v2026-05-29",
        title: "HUD más liviano en combate",
        category: "Performance",
        bullets: [
            "Antes: en cada hit/heal/regen el HUD entero se redibujaba (HP, Mana, inventario, mapa, hechizos, todo).",
            "Ahora: cuando solo cambia HP/Mana, solo la caja de stats se refresca. El minimapa y el resto del HUD ni se enteran.",
            "Lo mismo cuando se actualizan los dots del minimapa: solo esa caja reacciona, no el resto.",
            "Resultado: peleas largas contra varios enemigos dejan de stutterear; el cliente queda más fluido bajo carga.",
            "La caja del inventario y hechizos queda pendiente para una próxima iteración (menos urgente porque no cambia tan seguido)."
        ]
    },
    {
        version: "v2026-05-29",
        title: "Limpieza interna del servidor",
        category: "Performance",
        bullets: [
            "El servidor procesaba ciertas tareas (regenerar maná, expirar buffs) sobre jugadores que ya se habían desconectado, hasta por 60 segundos después.",
            "Ahora los descarta inmediatamente al cerrar la conexión.",
            "Mejora invisible al ojo pero acumula menos trabajo desperdiciado en sesiones con mucha entrada/salida de jugadores."
        ]
    },
    {
        version: "v2026-05-29",
        title: "Fix: stall del cliente al cambiar de mapa",
        category: "Performance",
        bullets: [
            "Tercer y último issue identificado en la investigación de freezes al cambiar de mapa.",
            "El refresco automático del minimapa (que dibuja a los enemigos cuando estás idle) corría cada 500ms sin pausa.",
            "Si ese refresco caía justo mientras el cliente estaba procesando la ráfaga de paquetes del mapa nuevo, sumaba un spike de trabajo en el peor momento.",
            "Fix: el refresco se silencia por 400ms al detectar un cambio de mapa y reanuda solo. Invisible al ojo, gratis para el resto del gameplay."
        ]
    },
    {
        version: "v2026-05-29",
        title: "Fix: crash en loop cuando un NPC mata a un user que se acaba de desconectar",
        category: "Performance",
        bullets: [
            "Race condition: si cerrabas la pestaña justo antes de que un NPC te matara, el server crasheaba en loop intentando dropear tus items al piso.",
            "Cada tick del NPC volvía a crashear hasta el cleanup automático de 60s — saturaba los logs y consumía CPU innecesario.",
            "Fix: guards defensivos en `tirarItemsUser` y `tirarItem` para retornar limpio si el ws ya no existe.",
            "Trade-off: el char no dropea loot al piso si se desconectó justo. Como ya no está en el server, recupera los items al reloguear."
        ]
    },
    {
        version: "v2026-05-29",
        title: "Fix: freezes al cambiar de mapas",
        category: "Performance",
        bullets: [
            "Antes: si el destino de un teletransporte estaba bloqueado (agua, otro player), el server buscaba un tile libre hasta 20.000 veces — congelaba el WS de todos los players online por 50-200ms.",
            "Ahora: cap bajado a 500 iteraciones (~2.5ms peor caso) — 40 veces más rápido el peor caso.",
            "Si no se encuentra tile libre tras 500 intentos, el char cae en Ullathorpe spawn (1, 50, 50) — en vez de ser kickeado con mensaje agresivo.",
            "Cero cambios para los teletransportes que funcionaban bien (la búsqueda válida típica termina en <50 intentos)."
        ]
    },
    {
        version: "v2026-05-29",
        title: "Party en el mapa + tu color por facción",
        category: "UX",
        bullets: [
            "Tu punto en el minimapa y mapa grande ahora es AZUL si sos ciudadano, ROJO si sos criminal.",
            "Nueva opción en settings: 'Mostrar party en el mapa' (No mostrar / Mostrar). Default: No mostrar.",
            "Si lo activás: tus compañeros de party aparecen como dots con su color de facción (no necesariamente el tuyo).",
            "En el mapa grande (M): panel 'Party · N' abajo a la izquierda con miembros del mismo mapa y dónde están.",
            "En el mapamundi: badge con cantidad de miembros sobre cada mapa donde haya alguien.",
            "El servidor manda posiciones live cada 1.5s — bajo costo de red."
        ]
    },
    {
        version: "v2026-05-29",
        title: "Mostrar enemigos en el mapa — 3 modos",
        category: "UX",
        bullets: [
            "En settings (rueda ⚙ caja 4) → 'Mostrar enemigos en el mapa' con 3 opciones: 'No mostrar', 'Solo viewport', 'Todo el mapa'.",
            "Solo viewport: enemigos visibles ahora mismo en tu pantalla (live).",
            "Todo el mapa: spawn points de todas las criaturas del mapa actual (heurística performante, criaturas errantes pueden estar a 1-3 tiles del spawn).",
            "En el mapa grande (M), click en el header 'Enemigos · N' colapsa o expande la lista. Los dots siguen visibles en el mapa igual.",
            "Performance: 100% cliente. Lazy-load del catálogo (37KB) solo cuando hace falta."
        ]
    },
    {
        version: "v2026-05-28",
        title: "Enemigos en el minimapa y mapa grande",
        category: "UX",
        bullets: [
            "El minimapa del HUD (esquina derecha) ahora muestra dots naranja para cada enemigo hostil del viewport.",
            "Al apretar M, el mapa grande también marca los enemigos y suma un panel abajo a la derecha con la lista (nombre + coords).",
            "Los NPCs útiles (Mercader, Gobernador, Sacerdote, Banquero) no se marcan — solo los hostiles.",
            "Refresco automático cada 500ms cuando estás idle pero los enemigos se mueven.",
            "Cero impacto en performance: solo se renderea lo que el cliente ya conoce del viewport."
        ]
    },
    {
        version: "v2026-05-28",
        title: "Macros guardados por personaje + cantidad en macroBar",
        category: "UX",
        bullets: [
            "Macros ahora se guardan en el personaje (DB), no en el browser. Si entrás desde otra PC tenés los mismos macros.",
            "MacroBar muestra la cantidad del item bindeado en tiempo real. Si tenés 696 pociones rojas, ves '696' arriba del slot — baja al usar.",
            "Cuenta el total sumando todos los stacks del mismo item (si splitéaste en varios slots).",
            "Migración invisible: macros guardados antes en el browser se suben automáticamente al servidor en el próximo login.",
            "Icono de Dardo Mágico revertido a la versión original."
        ]
    },
    {
        version: "v2026-05-28",
        title: "ESC pide confirmación antes de salir",
        category: "UX",
        bullets: [
            "Apretar ESC con nada abierto ahora muestra un cartel '¿Salir del juego?' con 'Salir' / 'Cancelar' en vez de tirarte al lobby de una.",
            "Salidas accidentales por tocar ESC sin querer mientras caminás: cero.",
            "ESC sobre el modal abierto lo cancela. Click en el fondo también cancela.",
            "El botón 'Salir' del navbar y el comando /salir en chat siguen saliendo directo — son acciones explícitas que no necesitan confirmación extra."
        ]
    },
    {
        version: "v2026-05-28",
        title: "Lobby: reordená tus personajes con drag & drop",
        category: "UX",
        bullets: [
            "Arrastrá un personaje del lobby a otro slot para intercambiarlos. Solo en la pestaña PvE.",
            "El orden queda guardado en tu cuenta — vuelve igual cuando relogueás o entrás desde otro browser.",
            "Visual: el char arrastrado se ve semitransparente, el slot destino se ilumina con borde dorado.",
            "Los personajes preset de PvP no son reordenables (no son personajes tuyos).",
            "Cero impacto en jugabilidad o data de chars — es metadata cosmética del lobby."
        ]
    },
    {
        version: "v2026-05-28",
        title: "Paladín y Asesino arrancan con maná",
        category: "Balance",
        bullets: [
            "Antes: Paladín y Asesino tenían 0 maná en L1 y recién a L2 podían castear. Inconsistente con las otras clases mágicas.",
            "Ahora: arrancan con int × 1 (~18-22 según raza), el mismo factor que ya ganaban por nivel.",
            "Paladines / Asesinos existentes recibieron +int de maxMana retroactivamente, equivalente al L1 que les faltaba.",
            "Sin cambios al per-level scaling — solo se corrigió el mismatch del nivel 1."
        ]
    },
    {
        version: "v2026-05-28",
        title: "Nombres de personajes case-insensitive",
        category: "Seguridad",
        bullets: [
            "Ya no se puede crear 'AtidorFA' si existe 'Atidorfa'. Tampoco 'atidorfa', 'ATIDORFA' ni cualquier variante de mayúsculas/minúsculas.",
            "Anti-impersonation: antes un troll podía registrar 'AtidorFA' y hacerse pasar por 'Atidorfa' en chat porque en fuente chica son indistinguibles.",
            "El nombre se sigue mostrando con el casing original que tipeás al crear el char. 'AtidorFA' se ve así, no lowercased.",
            "Migración de chars existentes ejecutada (24 chars). Un duplicado pre-existente (Herion/HerioN) se resolvió renombrando uno de ellos."
        ]
    },
    {
        version: "v2026-05-28",
        title: "Movimiento WASD smooth — la última tecla manda",
        category: "UX",
        bullets: [
            "Antes: holdear A y apretar W no hacía nada (A siempre ganaba). Ahora la última tecla apretada gana.",
            "Soltar la tecla activa cae natural a la previa que sigas holdeando: kitear y esquivar se sienten fluidos.",
            "Misma mecánica del AO clásico VB6 — cualquier secuencia de WASD responde sin pisarse.",
            "Bonus: si tabeás con una tecla holded, al volver el char no camina solo. Antes había que tocar la tecla de nuevo para destrabarse.",
            "Cero impacto en performance: stack máximo 5 entries, sin renders extra."
        ]
    },
    {
        version: "v2026-05-28",
        title: "Layout del juego responsive + fondo escala bien",
        category: "UX",
        bullets: [
            "El HUD de /play ahora se adapta a cualquier tamaño de pantalla.",
            "Pantallas chicas: si no entra todo, ahora hay scroll natural (antes el content quedaba recortado sin aviso).",
            "Pantallas grandes: el HUD queda alineado arriba con un margen pequeño en lugar de centrado vertical (que dejaba mucho espacio vacío).",
            "Imagen de fondo del site escala correctamente a cualquier viewport (antes se quedaba en tamaño natural y dejaba bandas negras).",
            "Sumamos una viñeta sutil que oscurece los bordes para dar foco visual al HUD.",
            "Las dimensiones internas del juego (canvas, panels, etc.) no cambiaron — solo cómo se posicionan en la página."
        ]
    },
    {
        version: "v2026-05-28",
        title: "Teclas custom por item de inventario",
        category: "UX",
        bullets: [
            "Click derecho sobre un item del inventario → 'Asignar tecla' / 'Quitar tecla'.",
            "Pociones, comida, scrolls: la tecla los CONSUME (igual que la tecla Usar).",
            "Armas, armaduras, escudos, cascos: la tecla EQUIPA/DESEQUIPA (igual que la tecla Equipar).",
            "Badge de la tecla aparece arriba a la derecha del icono del item.",
            "Anti-colisión total: la tecla no puede pisar a movement, macros, hechizos o otros items.",
            "Binding por item canónico: si movés la poción a otro slot, la tecla la sigue."
        ]
    },
    {
        version: "v2026-05-28",
        title: "Teclas custom por hechizo",
        category: "UX",
        bullets: [
            "Click derecho sobre un hechizo en la lista → opciones 'Asignar tecla' / 'Quitar tecla'.",
            "Presioná la tecla que querés y queda bindeada: en combate la presionás y el hechizo entra al modo 'click en target'.",
            "Validación anti-colisión: si la tecla ya está usada por movimiento, una acción o un macro, el modal te avisa y pide otra.",
            "Badge visual con la tecla asignada al lado del coste de maná en la lista de hechizos.",
            "Las teclas se guardan por personaje (no por cuenta). Sirven en el browser y persisten entre sesiones.",
            "ESC cancela la captura. La asignación vive en localStorage, no requiere viajar al server."
        ]
    },
    {
        version: "v2026-05-27",
        title: "Hogar configurable vía Gobernadores",
        category: "Gameplay",
        bullets: [
            "Right-click sobre cualquier Gobernador (npcType=11) → opción 'Hogar' en el context menu setea el hogar a tu posición actual.",
            "Validación server-side: target debe ser Gobernador, mismo mapa, distancia Chebyshev ≤ 3 tiles, char no muerto ni navegando.",
            "5 Gobernadores disponibles: Ullathorpe, Nix, Banderbill, Lindos, Arghal.",
            "Wire protocol bump: npcType (byte) ahora viaja en el packet getNpc; packet nuevo setHogarFromNpc (id 24).",
            "Copy de los Gobernadores actualizada: 'Haz click derecho para poner hogar aquí' (antes 'Escribe /Hogar para obtener la ciudadanía').",
            "Cierra la Fase 2 pendiente de /hogar que quedó documentada en la sesión 2026-05-26."
        ]
    },
    {
        version: "v2026-05-27",
        title: "NPC spell-cast AI: Bruja Inmovilizar + Medusa Paralizar",
        category: "Combat",
        bullets: [
            "Primer iteración de NPCs que castean spells. Sistema genérico: agregar castSpell + castCooldownMs al NPC en npcs.json y listo.",
            "Bruja (npc 550): Inmovilizar (idSpell 24) cada 10 segundos — el user no se mueve pero puede atacar/castear.",
            "Medusa (npc 553): Paralizar (idSpell 9) cada 7 segundos — lock total (no atacás, no castás, no te movés).",
            "Range del cast = viewportRadius (igual que players). No necesita estar adyacente.",
            "Cooldown desincronizado por instance: lastCastTime se inicializa con random offset para que múltiples Brujas no casteen sincrón.",
            "No re-cast sobre user ya lockeado: si user.paralizado o user.inmovilizado, el NPC espera a que expire.",
            "Polish: NPC gira sprite al target antes de castear. Broadcast de animFX + playSound + dialog (palabras mágicas) a vecinos en viewport.",
            "Cancela /hogar y runa de teletransportación: control spell cuenta como daño recibido.",
            "Cleanup pasivo intacto (server.js): user.paralizado/inmovilizado se limpian tras vars.timeInmo = 8000ms."
        ]
    },
    {
        version: "v2026-05-27",
        title: "UX: botón PARTY solo en tab Inventario",
        category: "UX",
        bullets: [
            "El botón PARTY al fondo del .body ahora solo se renderea cuando showInventary === true.",
            "En el tab Hechizos competía visualmente con la action row de reordenar/favoritos."
        ]
    },
    {
        version: "v2026-05-26",
        title: "Comando /hogar + tweak portal Dungeon Newbie",
        category: "Gameplay",
        bullets: [
            "/hogar (público): cast 5s con countdown, cooldown 5min, teleport al hogar del char (default Ullathorpe 1,50,50).",
            "Gating: solo en zona segura O muerto (modo fantasma) + bloqueado si navegando.",
            "Cancela el cast si: movimiento del user, daño de NPC o jugador (mele/spell PvP), o desconexión.",
            "Schema char extendido con hogarMap/X/Y (Mongoose defaults para chars pre-feature).",
            "Fase 2 pendiente: NPCs Gobernadores para asignar otras ciudades como hogar.",
            "Portal Dungeon Newbie (mapa 168, tiles 50-51 y=55): destino movido de (1,82,24) a (1,82,25)."
        ]
    },
    {
        version: "v2026-05-26",
        title: "Comando /w (susurros privados entre jugadores)",
        category: "Gameplay",
        bullets: [
            "/w Asuka mensaje — susurro 1-a-1 en color #DA6FD1 (rosa púrpura). Formato chat idéntico al público (italic).",
            "Comillas para nombres con espacios: /w \"j la rata\" hola. Parser maneja edge cases con mensajes claros.",
            "Solo emisor y receptor reciben los packets — el floating text sobre el emisor también está oculto a terceros.",
            "Prefill automático: después de /w Asuka, el siguiente Enter abre el chat con /w Asuka pre-rellenado (se limpia al mandar msg sin /w).",
            "Click izquierdo sobre [Nombre] en chat: abre el input con /w <name> pre-rellenado (auto-comillado si nombre con espacios).",
            "Bug colateral arreglado: React key collision en writeConsole (+new Date() duplicaba keys → msgs consecutivos del mismo tick se perdían en silencio)."
        ]
    },
    {
        version: "v2026-05-26",
        title: "Apuñalar: bugs barridos + visual ¡X! crítico",
        category: "Combat",
        bullets: [
            "apuUser CRIT-58 missing: clamp Math.max(0, hp - tmpDmg) para evitar HP negativo wire-side.",
            "apuUser updateHP broadcast missing: barra del target ahora refresca en el mismo tick que el chat 'te ha apuñalado'.",
            "Visual ¡X! crítico flotando sobre el char en color #FF2020 (rojo intenso, distinto del rojo común del hit normal).",
            "Stash + reverse-order send: apu dialog se manda DESPUÉS del normal hit dialog para sobrescribir (dialogs[id] es last-write-wins en cliente).",
            "Chat 'Has apuñalado por X' ahora en #FF2020 (antes rojo plano — distingue del 'Le has pegado').",
            "Mecánica intacta: probs por clase, multiplicadores ×1.4/×1.5/×2, lista de armas con apu:1 todo igual."
        ]
    },
    {
        version: "v2026-05-26",
        title: "Exp tiers endgame (L25+ grind escalado 2×/3×/4×)",
        category: "Balance",
        bullets: [
            "Tier 1 (L25-L34): subir cada nivel cuesta 2× la curva original (era 535k @ L25, ahora 1.07M).",
            "Tier 2 (L35-L39): cuesta 3× la curva original (era 6.17M @ L35, ahora 18.5M).",
            "Tier 3 (L40-L41): cuesta 4× la curva original (era 21.3M @ L41, ahora 85.1M).",
            "Multiplicadores ONE-TIME al cruzar boundary (L25/L35/L40) — el base mult propaga la proporción, sin compounding exponencial.",
            "Lifetime L1→L42: 110M → 343M exp totales (3,1× más para llegar al cap).",
            "Chars existentes no recalculan retroactivamente — solo el próximo level-up usa la curva nueva.",
            "Tabla completa OLD vs NEW en BALANCE.md → 'Comparativa OLD XP vs NEW XP'."
        ]
    },
    {
        version: "v2026-05-26",
        title: "Security audit completo (7 CRITICALs cerrados)",
        category: "Security",
        bullets: [
            "PUT /api/character_save: auth gate con token + log de intentos rechazados con IP.",
            "tokenAuth hardcoded → INTERNAL_SHARED_SECRET random de 64 bytes (env var via docker-compose).",
            "WS handshake con ticket HMAC-SHA256 firmado (TTL 60s) — el atacante ya no puede impersonar via IDs públicos del foro.",
            "Cookie session con SESSION_SECRET random + httpOnly + secure + sameSite=lax.",
            "NoSQL injection bloqueada: query parser simple + String() coerce explícito.",
            "Register validation completa: NAME_REGEX, EMAIL_REGEX, bcrypt cost 10 explícito.",
            "Username case drift: unique indexes en nameSanitized/email + findAccountsByNameSanitized."
        ]
    },
    {
        version: "v2026-05-26",
        title: "Anti-cheat gameplay + edge crashes",
        category: "Anti-cheat",
        bullets: [
            "attackSpell/attackRange ahora chequean distancia ≤ viewportRadius (mensaje 'Fuera de alcance' si lejos).",
            "attackMele bounds: atacar mirando al borde del mapa ya no crashea el server.",
            "eventClick / attackRange / attackSpell: bounds check en el fallback [y+1] para evitar mapData[101] undefined.",
            "quitarUserInvItem: clamp cant ≤ item.cant + guards defensivos (item undefined, cant ≤ 0).",
            "Paralyze auto-clear pasivo: user idle y NPC sin players cerca ahora se liberan automáticamente.",
            "Combat hp clamp Math.max(0, hp-dmg) en 5 sites (melee/spell/apuñalar): no más HP negativo transient.",
            "idItemArrow agregado al schema Mongoose (cazadores ahora mantienen arrow equipado entre sesiones)."
        ]
    },
    {
        version: "v2026-05-26",
        title: "Features rotas core funcionales",
        category: "Gameplay",
        bullets: [
            "Spell Resucitar (#11) ahora SÍ resucita: nueva rama en userSpellUser + bypass dead-check específico.",
            "ESC cascade: cierra modales/aim/menu antes de hacer logout (banco/trade/macro/map/chat input).",
            "Items fantasma post-reconnect: reset items/spells antes del poblado en getMyCharacter.",
            "NPCs fantasma post-teleport: clear engine.personajes (excepto self) en cambio de mapa.",
            "Meditar flag stuck post-muerte: limpia + broadcast animFX(0) en putBodyAndHeadDead.",
            "Listener leaks: handlers como this._handler* + cleanup en componentWillUnmount/engine.destroy().",
            "Sistema criminal half-baked: documentado pendiente (TODO #30 — fianza no decay todavía)."
        ]
    },
    {
        version: "v2026-05-26",
        title: "UX polish (Bucket 3 quick wins)",
        category: "UI/UX",
        bullets: [
            "Reconnect modal: spinner animado + 'Reconectando al servidor' (antes era pantalla negra sin feedback).",
            "Click spell no encontrado: writeConsole 'Hechizo no encontrado' (consistente con mana-insuficiente/cooldown).",
            "closeForce silent kick: modal warning rojo con lista de razones + botón 'Volver al lobby'.",
            "Block-walk feedback: 'No se puede caminar ahí' throttled 1.5s al chocar paredes/árboles.",
            "Class/race reject: mensaje en rojo bold con nombre del item ('Tu clase no puede equipar X')."
        ]
    },
    {
        version: "v2026-05-26",
        title: "Bugfixes varios (Bardo Laúd, racial armor, centering)",
        category: "Bugfix crítico",
        bullets: [
            "Bardo invisible al crear: workaround removiendo Laúd Mágico del kit (objType=18 mal categorizado en data — TODO docu).",
            "Armadura de Cuero Newbie ahora racial: enanos/gnomos reciben 1047 (sprite bajos) en vez de 1046 (sprite altos).",
            "Char centrado en viewport: cliente.viewportRadius 13→12 (canvas 21×21 → center pixel 336 alineado).",
            "TileBufferSize 3→5: fuente de Ullathorpe y otros sprites grandes ya no desaparecen al borde mid-animation.",
            "Crash 'vars.areaNpc[idNpc] is not iterable' en muereNpc: guard contra Set undefined.",
            "Pre-warm de páginas al boot: estilos cargan al primer click (era 'Ctrl+Shift+R' para forzar recompile).",
            "Mixed Content fix de ws-ticket en prod: API base ahora detecta localhost vs hosted (no más HTTPS→HTTP blocked)."
        ]
    },
    {
        version: "v2026-05-26",
        title: "Logo Cinzel + home news + foro launch",
        category: "Branding",
        bullets: [
            "Logo del navbar: Oswald → Cinzel (Trajan-style serif fantasy) con gradient gold metallic via background-clip:text.",
            "Layout pasa a 'Argentum Online · Web' en una línea (era 2 líneas), baseline-aligned.",
            "Home news con pinning visual (badge 'Fijado' + card Atrium dorado).",
            "Compromiso 'ningún personaje será borrado' explícito en la bienvenida + level cap actual = 42.",
            "3 noticias nuevas del 25-May (apertura oficial, banco Ullathorpe, NPCs inteligentes).",
            "Enlaces a Facebook removidos. Mención iOS/Android removida (compromiso solo cuando exista).",
            "Foro inaugurado con thread sticky de Atidorfa conmemorando el 25 de Mayo."
        ]
    },
    {
        version: "v2026-05-25",
        title: "Performance audit Tier 1-3",
        category: "Performance",
        bullets: [
            "WS setNoDelay(true) — disable Nagle en cada conexión: -20 a -40ms de ping promedio.",
            "Pool de ByteBuffer + cache HUD strings + Date.now() cacheado por frame: ~95% menos GC pressure en wire.",
            "TileBufferSize 9→3 + viewportRadius centralizado: ~30% menos drawcalls off-screen.",
            "visibilitychange listener + cap delta ≤50ms: mata el jank al volver del bg-tab.",
            "Audio: cache de localStorage + pool de 3 elements pre-creados por idSound: ~50ms/seg de main thread liberados.",
            "areaNpc Array→Set (~40 sitios): operaciones O(N)→O(1).",
            "user._invSize counter en hot path: reemplaza 5 Object.keys.length + descubre bug latente (slots 22-24 bloqueados silenciosamente).",
            "Mongoose indices + worldSave paralelo (Promise.all): ~30× más rápido con 30 chars conectados."
        ]
    },
    {
        version: "v2026-05-25",
        title: "Banco Goliath (NPC Banquero funcional)",
        category: "Gameplay",
        bullets: [
            "24 slots de banco persistentes en Mongo (campo bank en characters).",
            "Modal Atrium Frame con 2 grids 4×6 (banco | inv), drag-and-drop full.",
            "Partial stack split: depositar/retirar N divide stacks; mismo idItem en destino mergea.",
            "Wire protocol nuevo: openBank, bankUpdate, depositItem, withdrawItem, swapBankSlots.",
            "Anti-cheat: distance check ≤2 tiles al banquero + sesión npcBank activa.",
            "Persistencia en worldSave Y disconnect (crítico: sin la del disconnect se perderían depósitos recientes)."
        ]
    },
    {
        version: "v2026-05-25",
        title: "Combat SFX completo",
        category: "Audio",
        bullets: [
            "SFX en todos los paths: NPC↔user y PvP — block (escudo), hit (impacto), miss (swing).",
            "Broadcast vía loopArea para que chars cercanos escuchen el feedback ambiente.",
            "WAV fallback para IDs problemáticos del OGG Vorbis del cliente VB6 (Chrome no decodea).",
            "Fix de 3 bugs latentes de client.id undefined en callbacks de loopArea."
        ]
    },
    {
        version: "v2026-05-25",
        title: "Viewport refactor server↔cliente",
        category: "Bugfix crítico",
        bullets: [
            "Bug: chars al borde del viewport se evaporaban mid-step (cliente y server no acordaban en el rango).",
            "4 constantes linkeadas: canvasSize, viewportRadius (cliente), TileBufferSize, viewportRadius (server).",
            "~80 magic numbers refactoreados en game.js, protocol.js, npcs.js, socket.js.",
            "Click formula corregida: userCanvasTile = viewportRadius - 1.5 (antes 10.5 hardcoded → off-by-1 al subir radius).",
            "Documentación entera en CLAUDE.md con receta para escalar el viewport visible."
        ]
    },
    {
        version: "v2026-05-25",
        title: "Inventario 8×3 + fixes laterales",
        category: "UI/UX",
        bullets: [
            "Layout del inv HUD: 6×4 → 8×3 (mismo count, más horizontal). swapItems server bumpeado 1..21 → 1..24.",
            "Botón de ordenamiento de hechizos oculto (display:none) — funcionalidad viva por si vuelve.",
            "Chat msg al fallar maná en spell: italic azul (#7AB8F5), throttle 2s per-user.",
            "React {0 && <span>} bug en badge 'E' del banco: renderizaba '0' literal. Fix con !! + ternario.",
            "meditar:12 agregado a nameKeyCode (warning de React key undefined en modal de teclas)."
        ]
    },
    {
        version: "v2026-05-23",
        title: "Spell cast bug fix (idSpell canónico)",
        category: "Bugfix crítico",
        bullets: [
            "Lanzar Celeridad y casteaba Torpeza — slot/idPos drifteaba entre cliente y server.",
            "Wire protocol migrado a mandar idSpell canónico (nunca cambia).",
            "Server valida que el char conozca el hechizo escaneando user.spells.",
            "Inmune a: reordenamiento UI, learn/unlearn mid-action, desync de slots."
        ]
    },
    {
        version: "v2026-05-23",
        title: "Rogue Frame en todo el HUD",
        category: "Estética",
        bullets: [
            "Las 6 cajas del HUD (render, chat, header, body, stats, mapa) comparten exactamente el mismo chrome.",
            "Bautizadas tres recetas: Rogue Frame (in-game identidad), Slate Card (datos densos), Atrium Frame (out-of-game).",
            "Layout invariants documentados — bottoms de CAJA 1 y CAJA 6/7 alineados; tops de CAJA 2 y CAJA 4 alineados."
        ]
    },
    {
        version: "v2026-05-23",
        title: "Mapa redesign + plaques",
        category: "UI/UX",
        bullets: [
            "Sistema de 4 estados con tecla M: minimapa → currentMap → mapamundi → selectedMap.",
            "Plaques papyrus golden con nombre del mapa + coords.",
            "Right-click cierra al estado 0.",
            "Fix de portal loop infinito en Ullathorpe ↔ mapa 168."
        ]
    },
    {
        version: "v2026-05-23",
        title: "Balance documentado + Starter Kit",
        category: "Gameplay",
        bullets: [
            "HP nivel 1 corregido a 20 para todas las razas y clases.",
            "BALANCE.md inaugurado — fórmulas, tablas L1/L15/L25/L35/L42/L50.",
            "Starter kit con items auto-equipados (NEWBIE.md).",
            "Clases con maná arrancan con Dardo Mágico en slot 1."
        ]
    },
    {
        version: "v2026-05-23",
        title: "Identidad de marca",
        category: "Branding",
        bullets: [
            "Logo ARGENTUMONLINEWEB tricromático: celeste / blanco / naranja.",
            "Hover matrix-scan: beam dorado barre L→R en el navbar.",
            "Login button + form alineados a la estética elite minimal.",
            "Repo reuploadeado a github.com/atidorfa/ARGENTUM con autoría limpia."
        ]
    },
    {
        version: "v2026-05-23",
        title: "Naves de navegación",
        category: "Gameplay",
        bullets: [
            "Cada barco usa su sprite (anim del objeto) en lugar del hardcoded idBody 84.",
            "Slot equipped marca E en el inventario mientras navegás.",
            "noSeCae=1 en las 4 naves — ninguna se cae al morir navegando.",
            "Al morir navegando, sprite cambia a Fragata Fantasmal (idBody 87)."
        ]
    },
    {
        version: "v2026-05-23",
        title: "Wiki + Patch Notes inauguration",
        category: "Docs",
        bullets: [
            "PATCH_NOTES.md inaugurado en el root.",
            "WIKI.md actualizada con sección Patch Notes.",
            "Nueva página /wiki en el cliente."
        ]
    }
];

const RAZAS = [
    { name: "Humano",     mods: "+1 / +1 / 0 / +2",  const: 20, profile: "Balanceado, versátil. Buena vida, buen físico." },
    { name: "Elfo",       mods: "-1 / +3 / +2 / +1", const: 19, profile: "Ágil e inteligente. Bueno para magos y arqueros." },
    { name: "Elfo Drow",  mods: "+2 / +3 / +2 / 0",  const: 18, profile: "Híbrido (físico + magia). Vida baja." },
    { name: "Enano",      mods: "+3 / 0 / -2 / +3",  const: 21, profile: "El más fuerte y resistente. Inteligencia limitada." },
    { name: "Gnomo",      mods: "-2 / +3 / +4 / 0",  const: 18, profile: "El mago supremo. Frágil físicamente." }
];

const CLASES = [
    { name: "Mago",     type: "Mágico puro",     hp: 7,  mana: "int × 2.8", damage: "+1 hit", notes: "El más maná, el menos HP. Caster glass cannon." },
    { name: "Clérigo",  type: "Mágico",          hp: 8,  mana: "int × 2",   damage: "+2 hit", notes: "Sanador. Balance maná + vida + golpe básico." },
    { name: "Druida",   type: "Mágico",          hp: 8,  mana: "int × 2",   damage: "+2 hit", notes: "Doma criaturas, transformaciones." },
    { name: "Bardo",    type: "Mágico",          hp: 8,  mana: "int × 2",   damage: "+2 hit", notes: "Soporte. Maná decente, golpe medio." },
    { name: "Asesino",  type: "Semi-mágico",     hp: 8,  mana: "0 inicial", damage: "+3 hit", notes: "Sigilo + apuñalar (+140% con daga)." },
    { name: "Paladín",  type: "Semi-mágico",     hp: 9,  mana: "0 inicial", damage: "+3 hit", notes: "Tank con magia. Medita desde lvl 2." },
    { name: "Cazador",  type: "Físico distancia",hp: 9,  mana: "0",         damage: "+3 hit", notes: "Arco + flechas. Sin maná." },
    { name: "Guerrero", type: "Físico puro",     hp: 10, mana: "0",         damage: "+3 hit", notes: "Más HP y daño físico del juego." }
];

const NAVES = [
    { name: "Barca",             defense: "5 / 10",   hit: "10 / 25",  price: "100.000 oro ⚠️", tier: "Entry" },
    { name: "Galera",            defense: "10 / 20",  hit: "25 / 35",  price: "53.000 oro",     tier: "Mid" },
    { name: "Galeón",            defense: "15 / 25",  hit: "45 / 55",  price: "87.000 oro",     tier: "Top" },
    { name: "Fragata Fantasmal", defense: "0 / 0",    hit: "0 / 0",    price: "—",              tier: "Spirit" }
];

// Tabla de experiencia (base 300, multipliers ×1.4 / ×1.35 / ×1.3 /
// ×1.225 / ×1.25 según rango). Cap L42. `next` = exp para subir al
// siguiente nivel; `total` = lifetime exp acumulada para estar en
// ese nivel. Ver servidor/game.js:checkUserLevel + cliente/server/
// controllers/CharacterController.js (STARTING_EXP_NEXT_LEVEL = 300).
//
// Tiers de grind endgame (one-time multipliers en el boundary):
//   - L25-L34: ×2 (subir cualquier nivel de este rango cuesta 2× original)
//   - L35-L39: ×3
//   - L40-L41: ×4
// Ver BALANCE.md → "Experiencia por nivel".
const EXP_TABLE = [
    { level: 1,  next: 300,        total: 0 },
    { level: 2,  next: 420,        total: 300 },
    { level: 3,  next: 588,        total: 720 },
    { level: 4,  next: 823,        total: 1308 },
    { level: 5,  next: 1152,       total: 2131 },
    { level: 6,  next: 1613,       total: 3284 },
    { level: 7,  next: 2259,       total: 4897 },
    { level: 8,  next: 3162,       total: 7156 },
    { level: 9,  next: 4427,       total: 10318 },
    { level: 10, next: 6198,       total: 14746 },
    { level: 11, next: 8678,       total: 20944 },
    { level: 12, next: 12149,      total: 29622 },
    { level: 13, next: 17008,      total: 41770 },
    { level: 14, next: 23811,      total: 58779 },
    { level: 15, next: 32145,      total: 82590 },
    { level: 16, next: 43396,      total: 114735 },
    { level: 17, next: 58585,      total: 158132 },
    { level: 18, next: 79090,      total: 216717 },
    { level: 19, next: 106771,     total: 295807 },
    { level: 20, next: 144141,     total: 402578 },
    { level: 21, next: 187384,     total: 546719 },
    { level: 22, next: 243599,     total: 734103 },
    { level: 23, next: 316678,     total: 977702 },
    { level: 24, next: 411682,     total: 1294380 },
    { level: 25, next: 1070373,    total: 1706062 },
    { level: 26, next: 1391485,    total: 2776435 },
    { level: 27, next: 1808930,    total: 4167920 },
    { level: 28, next: 2351609,    total: 5976850 },
    { level: 29, next: 3057092,    total: 8328459 },
    { level: 30, next: 3974219,    total: 11385551 },
    { level: 31, next: 5166485,    total: 15359770 },
    { level: 32, next: 6716431,    total: 20526256 },
    { level: 33, next: 8227628,    total: 27242686 },
    { level: 34, next: 10078844,   total: 35470314 },
    { level: 35, next: 18519876,   total: 45549159 },
    { level: 36, next: 22686848,   total: 64069035 },
    { level: 37, next: 27791389,   total: 86755883 },
    { level: 38, next: 34044452,   total: 114547272 },
    { level: 39, next: 41704453,   total: 148591724 },
    { level: 40, next: 68117274,   total: 190296177 },
    { level: 41, next: 85146592,   total: 258413451 },
    { level: 42, next: null,       total: 343560043 }
];

const SECTIONS = [
    { id: "patchNotes", label: "Patch Notes",  icon: faClipboardList },
    { id: "razas",      label: "Razas",        icon: faUsers },
    { id: "clases",     label: "Clases",       icon: faShieldAlt },
    { id: "vidaMana",   label: "Vida y Maná",  icon: faHeartbeat },
    { id: "naves",      label: "Naves",        icon: faShip },
    { id: "hechizos",   label: "Hechizos",     icon: faMagic },
    { id: "exp",        label: "Experiencia",  icon: faChartLine },
    { id: "bestiario",  label: "Bestiario",    icon: faDragon }
];

// Tiers del bestiario (por exp x1) — color por dificultad.
const BEST_TIERS = [
    { label: "Newbie",       range: "exp ≤ 30",    color: "#7fae5a" },
    { label: "Baja",         range: "31 – 100",    color: "#74acdf" },
    { label: "Media",        range: "101 – 550",   color: "#a07fc0" },
    { label: "Alta",         range: "551 – 3.500", color: "#d48232" },
    { label: "Élite / Boss", range: "> 3.500",     color: "#e85d5d" }
];

const chanceClass = p =>
    p >= 90 ? "p90" : p >= 10 ? "p10" : p >= 1 ? "p1" : p >= 0.1 ? "p01" : "p001";

// Observer COMPARTIDO por todos los sprites del bestiario (sprite del NPC +
// los ~3 iconos de loot por fila = ~430 elementos). Uno solo en vez de 430.
// Cada elemento se desuscribe al primer hit; el sheet (/static/graficos/
// <f>.png) se carga SOLO al entrar al viewport. rootMargin precarga un poco
// antes. El crop es por CSS — no se generan imágenes nuevas.
let _spriteIO = null;
const _spriteCbs = typeof WeakMap !== "undefined" ? new WeakMap() : null;

function observeSprite(el, cb) {
    if (typeof IntersectionObserver === "undefined" || !_spriteCbs) {
        cb();
        return () => {};
    }
    if (!_spriteIO) {
        _spriteIO = new IntersectionObserver(
            entries => {
                entries.forEach(e => {
                    if (!e.isIntersecting) return;
                    const fn = _spriteCbs.get(e.target);
                    if (fn) {
                        fn();
                        _spriteIO.unobserve(e.target);
                        _spriteCbs.delete(e.target);
                    }
                });
            },
            { rootMargin: "300px" }
        );
    }
    _spriteCbs.set(el, cb);
    _spriteIO.observe(el);
    return () => {
        if (_spriteIO) _spriteIO.unobserve(el);
        _spriteCbs.delete(el);
    };
}

// Sprite cropeado de un sheet — sirve para el NPC (size grande) y para los
// iconos de ítems del loot (prop `size` chica). Lazy via observeSprite.
class NpcSprite extends React.Component {
    state = { show: false };
    ref = React.createRef();

    componentDidMount() {
        if (this.ref.current)
            this._unob = observeSprite(this.ref.current, () =>
                this.setState({ show: true })
            );
    }

    componentWillUnmount() {
        if (this._unob) this._unob();
    }

    render() {
        const s = this.props.sprite;
        const target = this.props.size || 44;
        if (!s) return <div className={style.spriteBox} ref={this.ref} />;
        const k = Math.min(2.2, target / s.h, (target + 8) / s.w);
        const box = {
            width: Math.round(s.w * k),
            height: Math.round(s.h * k)
        };
        const crop = { width: s.w, height: s.h, transform: `scale(${k})` };
        if (this.state.show) {
            crop.backgroundImage = `url(/static/graficos/${s.f}.png)`;
            crop.backgroundPosition = `-${s.x}px -${s.y}px`;
        }
        return (
            <div className={style.spriteBox} style={box} ref={this.ref}>
                <div className={style.spriteCrop} style={crop} />
            </div>
        );
    }
}

// Separador de miles "es-AR" → 1.234.567
const fmtNum = n => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

class WikiPage extends React.Component {
    static async getInitialProps({ req }) {
        const userAgent = req
            ? req.headers["user-agent"]
            : typeof navigator !== "undefined"
            ? navigator.userAgent
            : "";
        return { userAgent };
    }

    state = {
        activeSection: "patchNotes",
        bestiary: null,
        bestiaryLoading: false,
        bestiaryError: false,
        bestQuery: ""
    };

    setSection = id => {
        this.setState({ activeSection: id });
        if (
            id === "bestiario" &&
            !this.state.bestiary &&
            !this.state.bestiaryLoading
        ) {
            this.loadBestiary();
        }
    };

    // Carga el catálogo del bestiario una sola vez, al abrir la tab (regla
    // de perf: JSON pesado fuera del bundle, lazy al primer uso).
    loadBestiary = () => {
        this.setState({ bestiaryLoading: true });
        fetch("/static/init/bestiary.json")
            .then(r => r.json())
            .then(data =>
                this.setState({ bestiary: data, bestiaryLoading: false })
            )
            .catch(() =>
                this.setState({ bestiaryError: true, bestiaryLoading: false })
            );
    };

    renderPatchNotes = () => (
        <div className={style.patchList}>
            {PATCH_NOTES.map((entry, i) => (
                <div key={i} className={style.patchEntry}>
                    <div className={style.patchMeta}>
                        <span className={style.patchVersion}>
                            {entry.version}
                        </span>
                        <span className={style.patchCategory}>
                            {entry.category}
                        </span>
                        {entry.ticket ? (
                            <span
                                className={style.patchTicket}
                                title="Ticket interno (ver TICKETS.md)"
                            >
                                #{entry.ticket}
                            </span>
                        ) : null}
                    </div>
                    <h3 className={style.patchTitle}>{entry.title}</h3>
                    <ul className={style.patchBullets}>
                        {entry.bullets.map((b, j) => (
                            <li key={j}>{b}</li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );

    renderRazas = () => (
        <React.Fragment>
            <div className={style.tableHeader}>
                <div>Raza</div>
                <div>Δ Fue / Agi / Int / Con</div>
                <div className={style.alignCenter}>Const final</div>
                <div>Perfil</div>
            </div>
            <div className={style.tableBody}>
                {RAZAS.map((r, i) => (
                    <div key={i} className={style.row}>
                        <div className={style.colName}>{r.name}</div>
                        <div className={style.colMods}>{r.mods}</div>
                        <div className={style.alignCenter}>
                            <span className={style.levelBadge}>{r.const}</span>
                        </div>
                        <div className={style.colDesc}>{r.profile}</div>
                    </div>
                ))}
            </div>
            {/* Ticket #315: link a la página detallada con lore y stats
                completos por raza. */}
            <div style={{ textAlign: "center", marginTop: 18 }}>
                <Link href="/wiki/razas">
                    <a
                        style={{
                            display: "inline-block",
                            padding: "10px 22px",
                            background: "rgba(180, 130, 60, 0.25)",
                            border: "1px solid rgba(245, 175, 80, 0.5)",
                            borderRadius: 4,
                            color: "rgba(245, 175, 80, 0.95)",
                            textDecoration: "none",
                            fontFamily: "Cinzel, Tahoma, serif",
                            letterSpacing: 1,
                            fontSize: 13,
                            textTransform: "uppercase"
                        }}
                    >
                        Ver detalles completos →
                    </a>
                </Link>
            </div>
        </React.Fragment>
    );

    renderClases = () => (
        <React.Fragment>
            <div className={style.tableHeaderClases}>
                <div>Clase</div>
                <div>Tipo</div>
                <div className={style.alignCenter}>HP/nivel</div>
                <div className={style.alignCenter}>Maná</div>
                <div className={style.alignCenter}>Daño</div>
                <div>Notas</div>
            </div>
            <div className={style.tableBody}>
                {CLASES.map((c, i) => (
                    <div key={i} className={style.rowClases}>
                        <div className={style.colName}>{c.name}</div>
                        <div className={style.colType}>{c.type}</div>
                        <div className={style.alignCenter}>
                            <span className={style.levelBadge}>{c.hp}</span>
                        </div>
                        <div className={style.alignCenter}>{c.mana}</div>
                        <div className={style.alignCenter}>{c.damage}</div>
                        <div className={style.colDesc}>{c.notes}</div>
                    </div>
                ))}
            </div>

        </React.Fragment>
    );

    // Ticket #316: sección dedicada "Vida y Maná" — tablas L42 por raza
    // que reflejan el bonusVidaRaza (Enano/Elfo/Drow ganan +1 HP cada
    // nivel par). Mismo estilo visual que las otras tabs del wiki.
    renderVidaYMana = () => (
        <React.Fragment>
            <h3 className={style.subTableTitle}>HP a nivel 42 por raza</h3>
            <div className={style.tableHeaderRazas}>
                <div>Clase</div>
                <div className={style.alignCenter}>Enano</div>
                <div className={style.alignCenter}>Humano</div>
                <div className={style.alignCenter}>Elfo</div>
                <div className={style.alignCenter}>Drow</div>
                <div className={style.alignCenter}>Gnomo</div>
            </div>
            <div className={style.tableBody}>
                {[
                    ["Mago",     328, 307, 287, 287, 266],
                    ["Bardo",    369, 348, 328, 328, 307],
                    ["Druida",   369, 348, 328, 328, 307],
                    ["Clérigo",  369, 348, 328, 328, 307],
                    ["Asesino",  369, 348, 328, 328, 307],
                    ["Paladín",  410, 389, 369, 369, 348],
                    ["Cazador",  410, 389, 369, 369, 348],
                    ["Guerrero", 451, 430, 410, 410, 389]
                ].map((r, i) => (
                    <div key={i} className={style.rowRazas}>
                        <div className={style.colName}>{r[0]}</div>
                        <div className={style.alignCenter}>{r[1]}</div>
                        <div className={style.alignCenter}>{r[2]}</div>
                        <div className={style.alignCenter}>{r[3]}</div>
                        <div className={style.alignCenter}>{r[4]}</div>
                        <div className={style.alignCenter}>{r[5]}</div>
                    </div>
                ))}
            </div>

            <h3 className={style.subTableTitle}>Maná a nivel 42 por raza</h3>
            <div className={style.tableHeaderRazas}>
                <div>Clase</div>
                <div className={style.alignCenter}>Enano</div>
                <div className={style.alignCenter}>Humano</div>
                <div className={style.alignCenter}>Elfo</div>
                <div className={style.alignCenter}>Drow</div>
                <div className={style.alignCenter}>Gnomo</div>
            </div>
            <div className={style.tableBody}>
                {[
                    ["Mago",     1881, 2116, 2352, 2352, 2587],
                    ["Bardo",    1344, 1512, 1680, 1680, 1848],
                    ["Druida",   1344, 1512, 1680, 1680, 1848],
                    ["Clérigo",  1344, 1512, 1680, 1680, 1848],
                    ["Asesino",   672,  756,  840,  840,  924],
                    ["Paladín",   672,  756,  840,  840,  924],
                    ["Cazador",     0,    0,    0,    0,    0],
                    ["Guerrero",    0,    0,    0,    0,    0]
                ].map((r, i) => (
                    <div key={i} className={style.rowRazas}>
                        <div className={style.colName}>{r[0]}</div>
                        <div className={style.alignCenter}>{fmtNum(r[1])}</div>
                        <div className={style.alignCenter}>{fmtNum(r[2])}</div>
                        <div className={style.alignCenter}>{fmtNum(r[3])}</div>
                        <div className={style.alignCenter}>{fmtNum(r[4])}</div>
                        <div className={style.alignCenter}>{fmtNum(r[5])}</div>
                    </div>
                ))}
            </div>

            <div className={style.subTableNote}>
                Constitución por raza: <strong>Enano 21 · Humano 20 · Elfo/Drow 19 · Gnomo 18</strong>.
                Inteligencia: <strong>Enano 16 · Humano 18 · Elfo/Drow 20 · Gnomo 22</strong>.
                Enano / Elfo / Drow ganan <strong>+1 HP extra cada nivel par</strong> (bonusVidaRaza, ticket #316).
            </div>
        </React.Fragment>
    );

    renderNaves = () => (
        <React.Fragment>
            <div className={style.tableHeaderNaves}>
                <div>Nave</div>
                <div className={style.alignCenter}>Defensa</div>
                <div className={style.alignCenter}>Hit</div>
                <div className={style.alignCenter}>Precio</div>
                <div className={style.alignCenter}>Tier</div>
            </div>
            <div className={style.tableBody}>
                {NAVES.map((n, i) => (
                    <div key={i} className={style.rowNaves}>
                        <div className={style.colName}>{n.name}</div>
                        <div className={style.alignCenter}>{n.defense}</div>
                        <div className={style.alignCenter}>{n.hit}</div>
                        <div className={style.alignCenter}>{n.price}</div>
                        <div className={style.alignCenter}>
                            <span className={style.tierBadge}>{n.tier}</span>
                        </div>
                    </div>
                ))}
            </div>
            <div className={style.tableNote}>
                ⚠️ La Barca cuesta más que la Galera y el Galeón a pesar de ser
                la peor (anomalía heredada del AO original). La Fragata
                Fantasmal es la sprite del char muriendo en el mar — no se
                compra.
            </div>
        </React.Fragment>
    );

    renderHechizos = () => (
        <div className={style.spellSection}>
            <p className={style.lead}>
                Las clases con maná (mágicas y semi-mágicas) arrancan al nivel
                1 con <strong>Dardo Mágico</strong> aprendido en el slot 1 del
                libro de hechizos.
            </p>

            <div className={style.spellBlock}>
                <div className={style.spellTitle}>
                    Dardo Mágico
                    <span className={style.spellId}>idSpell 2</span>
                </div>
                <div className={style.spellStats}>
                    <div>
                        <span className={style.statLabel}>Costo</span>
                        <span className={style.statValue}>10 maná</span>
                    </div>
                    <div>
                        <span className={style.statLabel}>Daño</span>
                        <span className={style.statValue}>3-5 HP</span>
                    </div>
                    <div>
                        <span className={style.statLabel}>Skill mínimo</span>
                        <span className={style.statValue}>6</span>
                    </div>
                    <div>
                        <span className={style.statLabel}>Target</span>
                        <span className={style.statValue}>Enemigo</span>
                    </div>
                </div>
                <div className={style.spellWords}>"OHL VOR PEK"</div>
                <p className={style.spellDesc}>
                    Hechizo elemental de entrenamiento — bajo costo, daño
                    moderado, permite practicar magia desde el primer nivel.
                </p>
            </div>

            <div className={style.eligBlock}>
                <h4>¿Quién recibe Dardo Mágico?</h4>
                <ul>
                    <li>
                        <span className={style.yes}>✓</span> Mago, Clérigo,
                        Druida, Bardo
                    </li>
                    <li>
                        <span className={style.yes}>✓</span> Paladín, Asesino
                        (semi-mágicos)
                    </li>
                    <li>
                        <span className={style.no}>✗</span> Guerrero, Cazador,
                        Pirata, Trabajador, Ladrón (sin maná)
                    </li>
                </ul>
            </div>
        </div>
    );

    renderExp = () => (
        <React.Fragment>
            <p className={style.expLead}>
                Fórmula del server: <strong>300 exp</strong> base al nivel
                1, multipliers <strong>×1.4</strong> (L&lt;15) → <strong>
                ×1.35</strong> (L&lt;21) → <strong>×1.3</strong> (L&lt;33)
                → <strong>×1.225</strong> (L&lt;41) → <strong>×1.25</strong>
                (L41→42). Cap a nivel <strong>42</strong>.
            </p>
            <p className={style.expLead}>
                <strong>Tiers de grind endgame</strong>: subir cualquier nivel
                de <strong>L25-L34</strong> cuesta <strong>2×</strong> la curva
                original; <strong>L35-L39</strong> cuesta <strong>3×</strong>;
                <strong> L40-L41</strong> cuesta <strong>4×</strong>. Llegar a L42
                ahora requiere <strong>343.560.043 exp</strong> totales (3,1× más
                que con la curva base).
            </p>
            <div className={style.tableHeaderExp}>
                <div className={style.alignCenter}>Nivel</div>
                <div className={style.alignCenter}>Exp al siguiente</div>
                <div className={style.alignCenter}>Total acumulado</div>
            </div>
            <div className={style.tableBody}>
                {EXP_TABLE.map(r => (
                    <div key={r.level} className={style.rowExp}>
                        <div className={style.alignCenter}>
                            <span className={style.levelBadge}>{r.level}</span>
                        </div>
                        <div className={style.alignCenter}>
                            {r.next === null ? (
                                <span className={style.maxBadge}>MAX</span>
                            ) : (
                                fmtNum(r.next)
                            )}
                        </div>
                        <div className={style.alignCenter}>
                            {r.total === 0 ? "—" : fmtNum(r.total)}
                        </div>
                    </div>
                ))}
            </div>
            <div className={style.tableNote}>
                Al subir de nivel, <code>exp</code> se resetea a 0 (la barra
                empieza de cero hacia el próximo umbral). El total acumulado
                de la columna derecha es <strong>lifetime exp ganada</strong>
                {" "}para llegar a ese nivel.
            </div>
        </React.Fragment>
    );

    renderBestiario = () => {
        const { bestiary, bestiaryError, bestQuery } = this.state;

        if (bestiaryError)
            return (
                <div className={style.tableNote}>
                    No se pudo cargar el bestiario. Recargá la página.
                </div>
            );
        if (!bestiary)
            return <div className={style.bestLoading}>Cargando bestiario…</div>;

        const q = (bestQuery || "").toLowerCase();

        return (
            <div className={style.bestWrap}>
                <p className={style.bestIntro}>
                    {bestiary.length} criaturas hostiles · rates x1. HP, EXP,
                    oro y recompensa (loot) con la probabilidad de drop de cada
                    ítem por slot (90% / 10% / 1% / 0.1% / 0.01%).
                </p>
                <input
                    className={style.bestSearch}
                    placeholder="Buscar criatura…"
                    value={bestQuery}
                    onChange={e => this.setState({ bestQuery: e.target.value })}
                />
                {BEST_TIERS.map((t, ti) => {
                    const grp = bestiary.filter(
                        m => m.tier === ti && m.name.toLowerCase().includes(q)
                    );
                    if (!grp.length) return null;
                    return (
                        <div key={ti} className={style.bestSection}>
                            <div
                                className={style.bestTierLabel}
                                style={{ borderLeftColor: t.color }}
                            >
                                <b style={{ color: t.color }}>{t.label}</b>
                                <span>
                                    {t.range} · {grp.length}
                                </span>
                            </div>
                            <table className={style.bestTable}>
                                <thead>
                                    <tr>
                                        <th />
                                        <th className={style.thLeft}>
                                            Criatura
                                        </th>
                                        <th>HP</th>
                                        <th>EXP</th>
                                        <th>Oro</th>
                                        <th className={style.thLeft}>
                                            Recompensa
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {grp.map(m => (
                                        <tr key={m.name}>
                                            <td className={style.tdImg}>
                                                <NpcSprite sprite={m.spr} />
                                            </td>
                                            <td className={style.tdName}>
                                                {m.name}
                                            </td>
                                            <td className={style.tdHp}>
                                                {fmtNum(m.hp)}
                                            </td>
                                            <td className={style.tdXp}>
                                                {fmtNum(m.exp)}
                                            </td>
                                            <td className={style.tdGold}>
                                                {m.gold ? fmtNum(m.gold) : "—"}
                                            </td>
                                            <td className={style.tdLoot}>
                                                {m.loot.length === 0 ? (
                                                    <span
                                                        className={
                                                            style.bestNoLoot
                                                        }
                                                    >
                                                        —
                                                    </span>
                                                ) : (
                                                    m.loot.map((d, di) => (
                                                        <span
                                                            key={di}
                                                            className={`${
                                                                style.lootChip
                                                            } ${
                                                                d.m
                                                                    ? style.lootChipMoney
                                                                    : ""
                                                            }`}
                                                        >
                                                            <i
                                                                className={`${
                                                                    style.lootPct
                                                                } ${
                                                                    style[
                                                                        chanceClass(
                                                                            d.p
                                                                        )
                                                                    ]
                                                                }`}
                                                            >
                                                                {d.p}%
                                                            </i>
                                                            {d.ic ? (
                                                                <NpcSprite
                                                                    sprite={
                                                                        d.ic
                                                                    }
                                                                    size={20}
                                                                />
                                                            ) : null}
                                                            <span
                                                                className={
                                                                    style.lootText
                                                                }
                                                            >
                                                                {d.m
                                                                    ? `${fmtNum(
                                                                          d.c
                                                                      )} oro`
                                                                    : d.n +
                                                                      (d.c > 1
                                                                          ? ` ×${d.c}`
                                                                          : "")}
                                                            </span>
                                                        </span>
                                                    ))
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                })}
            </div>
        );
    };

    renderActiveSection = () => {
        switch (this.state.activeSection) {
            case "patchNotes": return this.renderPatchNotes();
            case "razas":      return this.renderRazas();
            case "clases":     return this.renderClases();
            case "vidaMana":   return this.renderVidaYMana();
            case "naves":      return this.renderNaves();
            case "hechizos":   return this.renderHechizos();
            case "exp":        return this.renderExp();
            case "bestiario":  return this.renderBestiario();
            default:           return null;
        }
    };

    render() {
        const { userAgent } = this.props;
        const { activeSection } = this.state;
        const active = SECTIONS.find(s => s.id === activeSection);

        return (
            <MainContainer userAgent={userAgent}>
                <div className={style.page}>
                    <div className={style.card}>
                        <div className={style.cardHeader}>
                            <FontAwesomeIcon icon={faBookOpen} />
                            <h2>Wiki</h2>
                            <span className={style.subtitle}>
                                {active.label}
                            </span>
                        </div>

                        <div className={style.tabs}>
                            {SECTIONS.map(s => (
                                <button
                                    key={s.id}
                                    type="button"
                                    className={`${style.tab} ${
                                        activeSection === s.id
                                            ? style.tabActive
                                            : ""
                                    }`}
                                    onClick={() => this.setSection(s.id)}
                                >
                                    <FontAwesomeIcon icon={s.icon} />
                                    <span>{s.label}</span>
                                </button>
                            ))}
                        </div>

                        <div className={style.content}>
                            {this.renderActiveSection()}
                        </div>

                        <div className={style.cardFooter}>
                            Compendio del fork — para detalle completo ver
                            PATCH_NOTES.md / WIKI.md en el repo.
                        </div>
                    </div>
                </div>
            </MainContainer>
        );
    }
}

function mapStateToProps(state) {
    return { account: state.account };
}

export default connect(mapStateToProps)(WikiPage);
